/**
 * USIPIC Product Configurator — full PDP (config API + canvas text overlay + Shopify variants)
 */
(function () {
  'use strict';

  var boot = null;
  var configData = null;
  var surface = null;
  var state = { values: {}, variantId: null };
  var choiceGridRenderIndex = 0;
  var loadedFonts = {};
  var fontMenuOutsideBound = false;
  var _thumbItemsKey = '';
  var _previewImageCache = {};
  var THUMB_VISIBLE_MAX = 4;

  /** Allowed Google Fonts — source: font/font.txt */
  var USIPIC_ALLOWED_FONTS = [
    'Roboto Condensed', 'Libre Baskerville', 'Outfit'
  ];

  var _allowedFontKeys = null;

  function getAllowedFontKeySet() {
    if (!_allowedFontKeys) {
      _allowedFontKeys = {};
      USIPIC_ALLOWED_FONTS.forEach(function (name) {
        _allowedFontKeys[normalizeKey(name)] = true;
      });
    }
    return _allowedFontKeys;
  }

  function isAllowedFontFamily(family) {
    if (!family) return false;
    return !!getAllowedFontKeySet()[normalizeKey(family)];
  }

  function getCustomFontUrl(source) {
    if (!source) return '';
    return String(
      source.customFontUrl || (source.textStyle && source.textStyle.customFontUrl) || ''
    ).trim();
  }

  function isRenderableFont(family, customFontUrl, googleFontCssUrl) {
    if (customFontUrl) return !!family;
    if (googleFontCssUrl) return !!family;
    return isAllowedFontFamily(family);
  }

  function isRenderableFontChoice(ch) {
    if (!ch) return false;
    return isRenderableFont(
      ch.fontFamily || ch.name,
      getCustomFontUrl(ch),
      ch.googleFontCssUrl
    );
  }

  var _customFontLoadPromises = {};

  function loadCustomFontUrl(family, url) {
    if (!family || !url) return Promise.resolve();
    var cacheKey = 'custom:' + normalizeKey(family) + '|' + url;
    if (_customFontLoadPromises[cacheKey]) return _customFontLoadPromises[cacheKey];
    var faceId = 'usipic-pcfg-fontface-' + normalizeKey(family);
    if (!document.getElementById(faceId)) {
      var styleEl = document.createElement('style');
      styleEl.id = faceId;
      styleEl.textContent =
        '@font-face{font-family:' +
        JSON.stringify(family) +
        ';src:url(' +
        JSON.stringify(url) +
        ') format("truetype");font-weight:400;font-style:normal;font-display:swap;}';
      document.head.appendChild(styleEl);
    }
    loadedFonts[cacheKey] = true;
    _customFontLoadPromises[cacheKey] = Promise.resolve().then(function () {
      if (document.fonts && document.fonts.load) {
        return document.fonts.load('400 16px ' + JSON.stringify(family)).catch(function () {});
      }
    });
    return _customFontLoadPromises[cacheKey];
  }

  function loadFontResource(fontLike) {
    if (!fontLike) return Promise.resolve();
    var family = fontLike.fontFamily || fontLike.name;
    var customUrl = getCustomFontUrl(fontLike);
    if (customUrl) return loadCustomFontUrl(family, customUrl);
    if (fontLike.googleFontCssUrl) {
      loadGoogleFontCssUrl(fontLike.googleFontCssUrl);
      return Promise.resolve();
    }
    if (shouldLoadWebFont(fontLike) && isAllowedFontFamily(family)) {
      loadGoogleFont(family);
    }
    return Promise.resolve();
  }

  var USIPIC_FRAME_ASSET_BASE = 'https://www.usipic.net/Frame-type/';
  var IMAGE_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;
  var activeImageOptId = null;
  var imageDragState = null;
  var imagePointerBound = false;
  var imageDeselectBound = false;
  /** Local files only — attached to Shopify cart/add.js on add-to-cart (stored on Shopify CDN). */
  var imageLocalFiles = {};

  function revokeLocalImage(optId) {
    var local = imageLocalFiles[optId];
    if (local && local.objectUrl) {
      try {
        URL.revokeObjectURL(local.objectUrl);
      } catch (e) {}
    }
    delete imageLocalFiles[optId];
  }

  function hasImageForOption(opt) {
    if (!opt) return false;
    return !!(imageLocalFiles[opt.id] || state.values[opt.id]);
  }

  function getImagePreviewSrc(optId) {
    var local = imageLocalFiles[optId];
    if (local && local.objectUrl) return local.objectUrl;
    var url = state.values[optId];
    return url ? resolvePreviewImageUrl(url) : '';
  }

  function setLocalImageFile(optId, file) {
    revokeLocalImage(optId);
    state.values[optId] = '';
    var objectUrl = URL.createObjectURL(file);
    imageLocalFiles[optId] = { file: file, objectUrl: objectUrl };
  }

  function imageFileExtension(file) {
    if (!file) return '.jpg';
    var name = file.name || '';
    var dot = name.lastIndexOf('.');
    if (dot > 0) return name.slice(dot).toLowerCase();
    if (file.type === 'image/png') return '.png';
    if (file.type === 'image/webp') return '.webp';
    return '.jpg';
  }

  function buildCustomizationJsonForCart() {
    var out = Object.assign({}, state.values);
    /* Every size owns its own frame option and only one is ever active, but the
       state keeps them all — so the JSON used to carry the frames of sizes the
       customer had merely clicked past. Keep only the selected size's frame.
       Compared case-insensitively: the size key is lower-case ("40x30") while the
       option id is not ("opt_frame_40X30"). */
    var activeFrameKey = ('opt_frame_' + getSelectedSizeKey()).toLowerCase();
    Object.keys(out).forEach(function (key) {
      if (/^opt_frame_/i.test(key) && key.toLowerCase() !== activeFrameKey) delete out[key];
    });
    getImageOptions().forEach(function (opt) {
      if (imageLocalFiles[opt.id]) {
        var local = imageLocalFiles[opt.id];
        out[opt.id] = '__shopify_file__:' + (local.file && local.file.name ? local.file.name : 'upload');
      }
    });
    return JSON.stringify(out);
  }

  function collectImageCartFiles() {
    var files = {};
    getImageOptions().forEach(function (opt) {
      var local = imageLocalFiles[opt.id];
      if (!local || !local.file) return;
      var label = cartPropertyLabel(opt);
      files[label] = {
        blob: local.file,
        filename: 'customer-upload' + imageFileExtension(local.file),
      };
    });
    return files;
  }

  function stateKeyImageTransform(optId) {
    return optId + '__transform';
  }

  function defaultImageTransform() {
    return { scale: 1, rotation: 0, offsetX: 0, offsetY: 0 };
  }

  function clampTransform(raw) {
    var t = raw || {};
    var scale = Number(t.scale);
    var rotation = Number(t.rotation);
    var offsetX = Number(t.offsetX);
    var offsetY = Number(t.offsetY);
    return {
      scale: isFinite(scale) ? Math.min(4, Math.max(0.25, scale)) : 1,
      rotation: isFinite(rotation) ? rotation : 0,
      offsetX: isFinite(offsetX) ? offsetX : 0,
      offsetY: isFinite(offsetY) ? offsetY : 0,
    };
  }

  function parseImageTransform(raw) {
    if (!raw) return defaultImageTransform();
    if (typeof raw === 'object') return clampTransform(raw);
    try {
      return clampTransform(JSON.parse(raw));
    } catch (e) {
      return defaultImageTransform();
    }
  }

  function getImageTransform(optId) {
    return parseImageTransform(state.values[stateKeyImageTransform(optId)]);
  }

  function setImageTransform(optId, transform) {
    state.values[stateKeyImageTransform(optId)] = JSON.stringify(clampTransform(transform));
  }

  function resolveDesignSize(surf) {
    if (!surf) return { width: 1200, height: 1200 };
    var dw = Number(surf.designWidth) || 400;
    var dh = Number(surf.designHeight) || 400;
    var inferW = 400;
    var inferH = 400;
    (surf.options || []).forEach(function (opt) {
      if (!opt.placement) return;
      var p = opt.placement;
      inferW = Math.max(inferW, (p.x || 0) + (p.width || 0) + 20);
      inferH = Math.max(inferH, (p.y || 0) + (p.height || 0) + 20);
    });
    if (dw > inferW * 1.5) dw = inferW;
    if (dh > inferH * 1.5) dh = inferH;
    return { width: dw, height: dh };
  }

  function placementToPercent(placement, dw, dh) {
    var p = placement || {};
    return {
      left: (100 * (p.x || 0)) / dw + '%',
      top: (100 * (p.y || 0)) / dh + '%',
      width: (100 * (p.width || 0)) / dw + '%',
      height: (100 * (p.height || 0)) / dh + '%',
    };
  }

  function designToStagePx(value, designSize, stageSize) {
    if (!designSize || !stageSize) return 0;
    return (Number(value) / designSize) * stageSize;
  }

  function applyMaskStyles(el, maskUrl) {
    if (!el || !maskUrl) return;
    var safe = resolvePreviewImageUrl(maskUrl);
    el.style.webkitMaskImage = 'linear-gradient(#fff, #fff), url("' + safe + '")';
    el.style.maskImage = 'linear-gradient(#fff, #fff), url("' + safe + '")';
    el.style.webkitMaskSize = '100% 100%, 100% 100%';
    el.style.maskSize = '100% 100%, 100% 100%';
    el.style.webkitMaskRepeat = 'no-repeat, no-repeat';
    el.style.maskRepeat = 'no-repeat, no-repeat';
    el.style.webkitMaskComposite = 'destination-out';
    el.style.maskComposite = 'exclude';
  }

  function getImageOptions() {
    if (!surface || !surface.options) return [];
    return surface.options.filter(function (opt) {
      return opt.type === 'image' && isOptionVisible(opt);
    });
  }

  function validateRequiredImages() {
    var missing = null;
    getImageOptions().forEach(function (opt) {
      if (!opt.required || missing) return;
      if (!hasImageForOption(opt)) missing = opt;
    });
    if (!missing) return { ok: true, message: '' };
    return { ok: false, message: 'Please upload ' + (missing.label || 'your image') + '.' };
  }

  function resolveCartFileEntry(key, entry) {
    if (!entry) return null;
    if (entry instanceof Blob) {
      return { blob: entry, filename: PCFG_CART_PREVIEW_FILE_KEYS[key] || 'file.jpg' };
    }
    if (entry.blob instanceof Blob) {
      return { blob: entry.blob, filename: entry.filename || 'file.jpg' };
    }
    return null;
  }

  function applyImageTransformStyle(transformEl, transform, stage) {
    if (!transformEl || !stage) return;
    var size = resolveDesignSize(surface);
    var stageW = stage.clientWidth || 1;
    var stageH = stage.clientHeight || 1;
    var ox = designToStagePx(transform.offsetX, size.width, stageW);
    var oy = designToStagePx(transform.offsetY, size.height, stageH);
    transformEl.style.transform =
      'translate(calc(-50% + ' + ox + 'px), calc(-50% + ' + oy + 'px)) rotate(' +
      transform.rotation +
      'deg) scale(' +
      transform.scale +
      ')';
  }

  function bindImagePointerEvents() {
    if (imagePointerBound) return;
    imagePointerBound = true;

    function endDrag() {
      imageDragState = null;
    }

    function onMove(e) {
      if (!imageDragState || !surface) return;
      var opt = getOptionById(imageDragState.optId);
      if (!opt || !opt.placement) return;
      var stage = qs('#usipic-pcfg-stage');
      if (!stage) return;
      var size = resolveDesignSize(surface);
      var stageW = stage.clientWidth || 1;
      var stageH = stage.clientHeight || 1;
      var dx = (e.clientX - imageDragState.startX) / stageW * size.width;
      var dy = (e.clientY - imageDragState.startY) / stageH * size.height;
      var t = Object.assign({}, imageDragState.startTransform);

      if (imageDragState.mode === 'move') {
        t.offsetX = imageDragState.startTransform.offsetX + dx;
        t.offsetY = imageDragState.startTransform.offsetY + dy;
      } else if (imageDragState.mode === 'scale') {
        var dist = Math.hypot(e.clientX - imageDragState.anchorX, e.clientY - imageDragState.anchorY);
        var base = imageDragState.startDist || 1;
        t.scale = imageDragState.startTransform.scale * (dist / base);
      } else if (imageDragState.mode === 'rotate') {
        var angle = Math.atan2(e.clientY - imageDragState.anchorY, e.clientX - imageDragState.anchorX);
        var startAngle = imageDragState.startAngle || 0;
        t.rotation = imageDragState.startTransform.rotation + (angle - startAngle) * (180 / Math.PI);
      }

      t = clampTransform(t);
      setImageTransform(opt.id, t);
      var slot = stage.querySelector('[data-image-option-id="' + opt.id + '"]');
      if (slot) {
        var transformEl = slot.querySelector('.usipic-pcfg__image-transform');
        applyImageTransformStyle(transformEl, t, stage);
      }
    }

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', endDrag);
    document.addEventListener('pointercancel', endDrag);
  }

  function startImageDrag(mode, optId, e, extra) {
    e.preventDefault();
    e.stopPropagation();
    activeImageOptId = optId;
    var t = getImageTransform(optId);
    imageDragState = {
      mode: mode,
      optId: optId,
      startX: e.clientX,
      startY: e.clientY,
      startTransform: Object.assign({}, t),
      anchorX: extra && extra.anchorX != null ? extra.anchorX : e.clientX,
      anchorY: extra && extra.anchorY != null ? extra.anchorY : e.clientY,
      startDist: extra && extra.startDist != null ? extra.startDist : 1,
      startAngle: extra && extra.startAngle != null ? extra.startAngle : 0,
    };
  }

  function appendImageHandles(container, opt) {
    var handles = document.createElement('div');
    handles.className = 'usipic-pcfg__image-handles';
    var corners = [
      { cls: 'nw', mode: 'scale' },
      { cls: 'ne', mode: 'scale' },
      { cls: 'sw', mode: 'scale' },
      { cls: 'se', mode: 'scale' },
    ];
    corners.forEach(function (c) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'usipic-pcfg__image-handle usipic-pcfg__image-handle--' + c.cls;
      btn.setAttribute('aria-label', 'Resize image');
      btn.addEventListener('pointerdown', function (e) {
        var rect = container.getBoundingClientRect();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var dist = Math.hypot(e.clientX - cx, e.clientY - cy) || 1;
        startImageDrag('scale', opt.id, e, { anchorX: cx, anchorY: cy, startDist: dist });
      });
      handles.appendChild(btn);
    });
    var rot = document.createElement('button');
    rot.type = 'button';
    rot.className = 'usipic-pcfg__image-handle usipic-pcfg__image-handle--rotate';
    rot.setAttribute('aria-label', 'Rotate image');
    rot.addEventListener('pointerdown', function (e) {
      var rect = container.getBoundingClientRect();
      var cx = rect.left + rect.width / 2;
      var cy = rect.top + rect.height / 2;
      var startAngle = Math.atan2(e.clientY - cy, e.clientX - cx);
      startImageDrag('rotate', opt.id, e, { anchorX: cx, anchorY: cy, startAngle: startAngle });
    });
    handles.appendChild(rot);
    container.appendChild(handles);
  }

  function pruneActiveImageSelection() {
    if (!activeImageOptId) return;
    var stillValid = getImageOptions().some(function (o) {
      return o.id === activeImageOptId && hasImageForOption(o);
    });
    if (!stillValid) activeImageOptId = null;
  }

  function shouldShowImageHandles(opt) {
    return hasImageForOption(opt) && activeImageOptId === opt.id;
  }

  function bindImageDeselectOutside() {
    if (imageDeselectBound) return;
    imageDeselectBound = true;
    document.addEventListener('pointerdown', function (e) {
      if (!activeImageOptId) return;
      if (
        e.target.closest('.usipic-pcfg__image-slot') ||
        e.target.closest('.usipic-pcfg__image-handles-frame') ||
        e.target.closest('.usipic-pcfg__image-handle')
      ) {
        return;
      }
      activeImageOptId = null;
      renderImageLayers();
    });
  }

  function bindImageSlotPointer(slot, opt) {
    slot.addEventListener('pointerdown', function (e) {
      if (e.target.closest('.usipic-pcfg__image-handle')) return;
      var wasActive = activeImageOptId === opt.id;
      activeImageOptId = opt.id;
      if (!wasActive) renderImageLayers();
      startImageDrag('move', opt.id, e);
    });
  }

  function appendImageHandlesOverlay(parent, opt, pct) {
    var frame = document.createElement('div');
    frame.className = 'usipic-pcfg__image-handles-frame is-active';
    frame.dataset.imageOptionId = opt.id;
    frame.style.left = pct.left;
    frame.style.top = pct.top;
    frame.style.width = pct.width;
    frame.style.height = pct.height;
    bindImageSlotPointer(frame, opt);
    appendImageHandles(frame, opt);
    parent.appendChild(frame);
  }

  function clearImageHandlesLayer() {
    var handlesRoot = qs('#usipic-pcfg-image-handles-layer');
    if (handlesRoot) handlesRoot.innerHTML = '';
  }

  function renderImageLayers() {
    var root = qs('#usipic-pcfg-image-layer');
    var handlesRoot = qs('#usipic-pcfg-image-handles-layer');
    var stage = qs('#usipic-pcfg-stage');
    if (!root || !stage || !surface || isPlainPreviewActive()) {
      if (root) root.innerHTML = '';
      clearImageHandlesLayer();
      return;
    }

    var imageOpts = getImageOptions();
    var hasAny = imageOpts.some(function (opt) {
      return hasImageForOption(opt);
    });
    if (!hasAny) {
      root.innerHTML = '';
      clearImageHandlesLayer();
      return;
    }

    pruneActiveImageSelection();

    var size = resolveDesignSize(surface);
    var maskUrl = surface.clippingMaskUrl || '';
    root.innerHTML = '';
    clearImageHandlesLayer();

    var maskWrap = document.createElement('div');
    maskWrap.className = 'usipic-pcfg__mask-layer';
    if (maskUrl) applyMaskStyles(maskWrap, maskUrl);

    imageOpts.forEach(function (opt) {
      if (!hasImageForOption(opt) || !opt.placement) return;
      var pct = placementToPercent(opt.placement, size.width, size.height);
      var slot = document.createElement('div');
      slot.className = 'usipic-pcfg__image-slot is-editable';
      slot.dataset.imageOptionId = opt.id;
      slot.style.left = pct.left;
      slot.style.top = pct.top;
      slot.style.width = pct.width;
      slot.style.height = pct.height;

      var transformWrap = document.createElement('div');
      transformWrap.className = 'usipic-pcfg__image-transform';
      var img = document.createElement('img');
      img.alt = '';
      img.draggable = false;
      img.src = getImagePreviewSrc(opt.id);
      transformWrap.appendChild(img);
      slot.appendChild(transformWrap);

      var transform = getImageTransform(opt.id);
      applyImageTransformStyle(transformWrap, transform, stage);

      bindImageSlotPointer(slot, opt);
      maskWrap.appendChild(slot);

      if (shouldShowImageHandles(opt) && handlesRoot) {
        appendImageHandlesOverlay(handlesRoot, opt, pct);
      }
    });

    root.appendChild(maskWrap);
    bindImagePointerEvents();
    bindImageDeselectOutside();
  }

  function appendImageUploadRow(block, opt) {
    var wrap = document.createElement('div');
    wrap.className = 'usipic-pcfg__upload';

    var inputId = 'usipic-pcfg-upload-' + String(opt.id).replace(/[^a-z0-9_-]/gi, '-');
    var input = document.createElement('input');
    input.type = 'file';
    input.id = inputId;
    input.className = 'usipic-pcfg__upload-file';
    input.accept = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';

    var hint = document.createElement('p');
    hint.className = 'usipic-pcfg__upload-hint';
    hint.textContent = opt.instructions || 'JPEG or PNG, max 15MB.';

    function setHint(msg, isError) {
      hint.textContent = msg || '';
      hint.classList.toggle('is-error', !!isError);
    }

    function renderUploadUi() {
      wrap.innerHTML = '';
      var hasImage = hasImageForOption(opt);
      if (hasImage) {
        var preview = document.createElement('div');
        preview.className = 'usipic-pcfg__upload-preview';
        var thumb = document.createElement('img');
        thumb.className = 'usipic-pcfg__upload-thumb';
        thumb.src = getImagePreviewSrc(opt.id);
        thumb.alt = '';
        var changeBtn = document.createElement('button');
        changeBtn.type = 'button';
        changeBtn.className = 'usipic-pcfg__upload-change';
        changeBtn.textContent = 'Change image';
        changeBtn.addEventListener('click', function () {
          input.click();
        });
        preview.appendChild(thumb);
        preview.appendChild(changeBtn);
        wrap.appendChild(preview);
      } else {
        var label = document.createElement('label');
        label.className = 'usipic-pcfg__upload-picker';
        label.setAttribute('for', inputId);
        label.textContent = opt.label || 'Upload image';
        wrap.appendChild(label);
      }
      wrap.appendChild(input);
      if (opt.required && !hasImage) {
        setHint('Required — please upload ' + (opt.label || 'your image') + '.', true);
      }
      wrap.appendChild(hint);
    }

    input.addEventListener('change', function () {
      var file = input.files && input.files[0];
      input.value = '';
      if (!file) return;
      if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
        setHint('File too large. Maximum size is 15MB.', true);
        return;
      }
      setLocalImageFile(opt.id, file);
      setImageTransform(opt.id, defaultImageTransform());
      activeImageOptId = null;
      setHint('', false);
      renderUploadUi();
      renderImageLayers();
      refreshDesignPreview();
      updatePriceUI(pickActivePricing());
    });

    renderUploadUi();
    block.appendChild(wrap);
  }

  function drawImageContain(ctx, img, dw, dh) {
    if (!ctx || !img) return;
    var iw = img.naturalWidth || img.width || 1;
    var ih = img.naturalHeight || img.height || 1;
    var scale = Math.min(dw / iw, dh / ih);
    var w = iw * scale;
    var h = ih * scale;
    var x = (dw - w) / 2;
    var y = (dh - h) / 2;
    ctx.drawImage(img, x, y, w, h);
  }

  function drawUploadOnCanvas(ctx, img, placement, transform) {
    if (!ctx || !img || !placement) return;
    var p = placement;
    var t = clampTransform(transform);
    var cx = p.x + p.width / 2 + t.offsetX;
    var cy = p.y + p.height / 2 + t.offsetY;
    ctx.save();
    ctx.beginPath();
    ctx.rect(p.x, p.y, p.width, p.height);
    ctx.clip();
    ctx.translate(cx, cy);
    ctx.rotate((t.rotation * Math.PI) / 180);
    ctx.scale(t.scale, t.scale);
    var iw = img.naturalWidth || img.width || 1;
    var ih = img.naturalHeight || img.height || 1;
    var coverScale = Math.max(p.width / iw, p.height / ih);
    var w = iw * coverScale;
    var h = ih * coverScale;
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    ctx.restore();
  }

  function drawUploadedImagesOnCanvas(ctx, dw, dh) {
    if (!ctx || !surface) return Promise.resolve();
    var imageOpts = getImageOptions().filter(function (opt) {
      return hasImageForOption(opt) && opt.placement;
    });
    if (!imageOpts.length) return Promise.resolve();

    var uploadCanvas = document.createElement('canvas');
    uploadCanvas.width = dw;
    uploadCanvas.height = dh;
    var uctx = uploadCanvas.getContext('2d');
    if (!uctx) return Promise.resolve();

    var chain = Promise.resolve();
    imageOpts.forEach(function (opt) {
      chain = chain.then(function () {
        return loadImageForCanvas(getImagePreviewSrc(opt.id)).then(function (img) {
          if (img) drawUploadOnCanvas(uctx, img, opt.placement, getImageTransform(opt.id));
        });
      });
    });

    return chain.then(function () {
      var maskUrl = surface.clippingMaskUrl;
      if (!maskUrl) {
        ctx.drawImage(uploadCanvas, 0, 0);
        return;
      }
      return loadImageForCanvas(maskUrl).then(function (maskImg) {
        if (!maskImg) {
          ctx.drawImage(uploadCanvas, 0, 0);
          return;
        }
        var masked = document.createElement('canvas');
        masked.width = dw;
        masked.height = dh;
        var mctx = masked.getContext('2d');
        if (!mctx) {
          ctx.drawImage(uploadCanvas, 0, 0);
          return;
        }
        mctx.drawImage(uploadCanvas, 0, 0);
        mctx.globalCompositeOperation = 'destination-out';
        mctx.drawImage(maskImg, 0, 0, dw, dh);
        ctx.drawImage(masked, 0, 0);
      });
    });
  }

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function readBoot() {
    var el = document.getElementById('usipic-pcfg-bootstrap');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function normalizeKey(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  function cartAddUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.slice(-1) !== '/') root += '/';
    return root + 'cart/add.js';
  }

  function getMoneyFormat() {
    if (boot && boot.moneyFormat) return boot.moneyFormat;
    if (window.T4Sconfigs && window.T4Sconfigs.moneyFormat) return window.T4Sconfigs.moneyFormat;
    if (window.Shopify && window.Shopify.money_format) return window.Shopify.money_format;
    return '${{amount}}';
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(cents) || cents == null) return '0';
    var amount = (Number(cents) / 100).toFixed(precision);
    var parts = amount.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] != null && precision > 0 ? dollars + decimal + parts[1] : dollars;
  }

  function formatMoney(cents) {
    if (cents == null || cents === '') return '';
    var formatString = getMoneyFormat();
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = formatString.match(placeholder);
    var value = '';
    if (!match) return formatWithDelimiters(cents, 2);
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholder, value);
  }

  function showError(msg) {
    var el = qs('#usipic-pcfg-error');
    if (!el) return;
    if (msg) {
      el.textContent = msg;
      el.hidden = false;
    } else {
      el.hidden = true;
      el.textContent = '';
    }
  }

  function setLoading(on) {
    var el = qs('#usipic-pcfg-loading');
    if (el) el.hidden = !on;
  }

  function setStatus(msg, isError) {
    var el = qs('#usipic-pcfg-status');
    if (!el) return;
    el.textContent = msg || '';
    el.classList.toggle('is-error', !!isError);
  }

  function configUrl() {
    var base = (boot.apiBase || '').replace(/\/$/, '');
    var path = boot.apiPath || '/api/customize/config';
    if (path.charAt(0) !== '/') path = '/' + path;
    return base + path + '?sku=' + encodeURIComponent(boot.configSku || '');
  }

  /** Config API may return duplicate option ids (e.g. text fields repeated per template row). */
  function dedupeOptionsById(options) {
    if (!options || !options.length) return options || [];
    var seen = {};
    var out = [];
    options.forEach(function (opt) {
      var key = String(opt && opt.id != null ? opt.id : '');
      if (!key || seen[key]) return;
      seen[key] = true;
      out.push(opt);
    });
    return out;
  }

  function fetchConfig() {
    return fetch(configUrl(), { method: 'GET', credentials: 'omit', headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Config API HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json || json.success === false) throw new Error((json && json.message) || 'Config API failed');
        configData = json.data || json;
        var surfaces = configData.surfaces;
        if (!surfaces || !surfaces.length) throw new Error('No surfaces in config');
        surface = surfaces[0];
        if (surface.options && surface.options.length > 1) {
          surface.options = dedupeOptionsById(surface.options);
        }
        preloadConfigFonts();
        return configData;
      });
  }

  function stateKeyColor(optId) {
    return optId + '__color';
  }

  function stateKeyFont(optId) {
    return optId + '__font';
  }

  var COLOR_CUSTOM_ID = '__custom__';

  function stateKeyCustomHex(optId) {
    return stateKeyColor(optId) + '__hex';
  }

  function normalizeColorHex(c) {
    if (!c) return '';
    var s = String(c).trim().toLowerCase();
    if (s.indexOf('rgb') === 0) {
      var m = s.match(/\d+/g);
      if (m && m.length >= 3) {
        return (
          '#' +
          [m[0], m[1], m[2]]
            .map(function (n) {
              var h = parseInt(n, 10).toString(16);
              return h.length === 1 ? '0' + h : h;
            })
            .join('')
        );
      }
    }
    return s;
  }

  function googleFontToChoice(name) {
    var safe = String(name || '').trim();
    if (!safe) return null;
    return {
      id: normalizeKey(safe) || safe,
      name: safe,
      fontFamily: safe,
      cssFontFamily: '"' + safe + '", sans-serif',
      googleFontCssUrl:
        'https://fonts.googleapis.com/css2?family=' +
        encodeURIComponent(safe).replace(/%20/g, '+') +
        ':wght@400;700&display=swap',
    };
  }

  function getCatalogFontChoices(opt) {
    var seen = {};
    var out = [];
    function add(ch) {
      if (!ch || !ch.id || seen[ch.id]) return;
      if (!isRenderableFontChoice(ch)) return;
      seen[ch.id] = true;
      out.push(ch);
    }
    if (opt) {
      var fromStyle = fontChoiceFromTextStyle(opt);
      if (fromStyle) add(fromStyle);
    }
    USIPIC_ALLOWED_FONTS.forEach(function (name) {
      add(googleFontToChoice(name));
    });
    return out;
  }

  function buildGoogleFontCssUrl(family) {
    if (!family || isSystemFontFamily(family)) return '';
    return (
      'https://fonts.googleapis.com/css2?family=' +
      encodeURIComponent(String(family).trim()).replace(/%20/g, '+') +
      '&display=swap'
    );
  }

  function isSystemFontFamily(family) {
    return /^(Arial|Helvetica|Times New Roman|Georgia|Courier( New)?|Verdana|Tahoma|Impact|serif|sans-serif|monospace)$/i.test(
      String(family || '').trim()
    );
  }

  function shouldLoadWebFont(ch) {
    if (!ch || !ch.fontFamily) return false;
    if (ch.googleFontCssUrl) return true;
    return !isSystemFontFamily(ch.fontFamily);
  }

  function fontChoiceFromTextStyle(opt) {
    if (!opt) return null;
    var ts = opt.textStyle || {};
    var family = ts.fontFamily || opt.fontFamily;
    if (!family) return null;
    var cssFamily = ts.cssFontFamily || opt.cssFontFamily || ('"' + family + '", serif');
    var customUrl = getCustomFontUrl(opt);
    var cssUrl = customUrl ? '' : ts.googleFontCssUrl || opt.googleFontCssUrl || buildGoogleFontCssUrl(family);
    return {
      id: normalizeKey(family) || family,
      name: family,
      fontFamily: family,
      cssFontFamily: cssFamily,
      googleFontCssUrl: cssUrl,
      customFontUrl: customUrl,
    };
  }

  function getConfiguredTextFontChoice(opt, fonts) {
    if (!opt || !fonts || !fonts.length) return null;
    if (opt.defaultFontId) {
      var byDefault = getChoiceFromList(fonts, opt.defaultFontId);
      if (byDefault) return byDefault;
    }
    var fromStyle = fontChoiceFromTextStyle(opt);
    if (!fromStyle) return null;
    var byId = getChoiceFromList(fonts, fromStyle.id);
    if (byId) return byId;
    for (var i = 0; i < fonts.length; i++) {
      var c = fonts[i];
      if (normalizeKey(c.fontFamily || c.name) === normalizeKey(fromStyle.fontFamily)) return c;
    }
    if (isRenderableFont(fromStyle.fontFamily, fromStyle.customFontUrl, fromStyle.googleFontCssUrl)) return fromStyle;
    return null;
  }

  function getDefaultTextFontChoice(opt, fonts) {
    return getConfiguredTextFontChoice(opt, fonts) || (fonts && fonts[0]) || null;
  }

  function findFontChoiceByFamily(fonts, family) {
    if (!fonts || !family) return null;
    var key = normalizeKey(family);
    for (var i = 0; i < fonts.length; i++) {
      var c = fonts[i];
      if (normalizeKey(c.fontFamily || c.name) === key) return c;
    }
    return null;
  }

  function hasTextStylePickers(opt) {
    if (!opt || opt.type !== 'text') return false;
    if (opt.fontChoices && opt.fontChoices.length) return true;
    if (opt.colorChoices && opt.colorChoices.length) return true;
    return !!(opt.textStyle || opt.placement);
  }

  function getTextFontChoices(opt) {
    if (!opt) return [];
    return getCatalogFontChoices(opt);
  }

  function getDefaultFallbackColors(opt) {
    return [
      { id: 'brown', name: 'Brown', color: '#594d3e' },
      { id: 'black', name: 'Black', color: '#111111' },
      { id: 'charcoal', name: 'Charcoal', color: '#333333' },
      { id: 'cream', name: 'Cream', color: '#f5f0e8' },
      { id: 'white', name: 'White', color: '#ffffff' },
    ];
  }

  function getPresetColors(opt, fontChoice) {
    var fromApi = null;
    if (fontChoice && fontChoice.colorChoices && fontChoice.colorChoices.length) {
      fromApi = fontChoice.colorChoices;
    } else if (opt && opt.colorChoices && opt.colorChoices.length) {
      fromApi = opt.colorChoices;
    } else if (configData && configData.meta && configData.meta.colorChoices && configData.meta.colorChoices.length) {
      fromApi = configData.meta.colorChoices;
    }
    if (fromApi && fromApi.length) return fromApi.slice(0, 5);
    if (!opt || hasTextStylePickers(opt) || opt.type === 'font_color' || opt.type === 'text_color' || opt.type === 'color') {
      return getDefaultFallbackColors(opt || {});
    }
    return [];
  }

  function resolveSelectedColor(optId, opt, fontChoice) {
    var presets = getPresetColors(opt, fontChoice);
    var selId = state.values[stateKeyColor(optId)];
    if (selId === COLOR_CUSTOM_ID) {
      var hex = state.values[stateKeyCustomHex(optId)] || '#111111';
      return { id: COLOR_CUSTOM_ID, name: 'Custom', color: hex };
    }
    return getChoiceFromList(presets, selId) || presets[0] || { color: '#111111' };
  }

  function isFontOptionType(opt) {
    if (!opt) return false;
    if (opt.type === 'font_family' || opt.type === 'font') return true;
    if (opt.type === 'text') {
      if (opt.fontChoices && opt.fontChoices.length) return false;
      return !!(opt.choices && opt.choices.length && opt.choices.some(function (c) {
        return c.fontFamily || c.cssFontFamily || c.googleFontCssUrl;
      }));
    }
    return !!(opt.fontChoices && opt.fontChoices.length);
  }

  function getFontChoices(opt) {
    if (!opt) return [];
    return getCatalogFontChoices(opt);
  }

  function getChoiceFromList(choices, choiceId) {
    if (!choices) return null;
    for (var i = 0; i < choices.length; i++) {
      if (choices[i].id === choiceId) return choices[i];
    }
    return null;
  }

  function isChoiceEnabled(ch) {
    return !!(ch && ch.enabled !== false);
  }

  var SIZE_KEY_STATE = '__usipic_size_key__';

  function getCatalogChoices(opt) {
    if (!opt || !opt.choices) return [];
    return opt.choices
      .filter(isChoiceEnabled)
      .slice()
      .sort(function (a, b) { return (a.order || 0) - (b.order || 0); });
  }

  function getPerFrameSizeOptions() {
    if (!surface || !surface.options) return [];
    return surface.options.filter(function (opt) {
      return /^opt_size_/.test(String(opt.id || ''));
    });
  }

  function usesPerFrameSizeMatrix() {
    return getPerFrameSizeOptions().length > 0;
  }

  function isAggregatedSizeOption(opt) {
    return usesPerFrameSizeMatrix() && String(opt.id || '').indexOf('opt_size_') === 0;
  }

  function extractSizeKeyFromChoice(ch) {
    if (!ch) return '';
    var m = String(ch.id || '').match(/size_(\d+x\d+)/i);
    if (m) return m[1].toLowerCase();
    var name = String(ch.name || '').trim();
    var m2 = name.match(/(\d+\s*[×x]\s*\d+)/);
    if (m2) return m2[1].replace(/\s/g, '').toLowerCase().replace('\u00d7', 'x');
    return normalizeKey(name);
  }

  function getFrameCodeFromChoice(ch) {
    if (!ch) return '';
    var id = String(ch.id || '');
    var codeFromId = id.match(/frame_((?:F|L|C)\d+)/i);
    if (codeFromId) return codeFromId[1].toUpperCase();
    var m = id.match(/^frame_(.+)$/i);
    if (m && /^(?:F|L|C)\d+$/i.test(m[1])) return String(m[1]).toUpperCase();
    var name = String(ch.name || '').trim();
    var code = name.match(/\b(F\d+|L\d+|C\d+)\b/i);
    if (code) return code[1].toUpperCase();
    return name;
  }

  function getSizeOptionForFrameCode(frameCode) {
    if (!frameCode) return null;
    var code = String(frameCode).trim();
    var short = code.match(/^(F\d+|L\d+|C\d+)/i);
    var key = short ? short[1].toUpperCase() : code.replace(/\s*\([^)]*\)\s*/g, '').trim();
    var direct = getOptionById('opt_size_' + key);
    if (direct) return direct;
    if (!surface || !surface.options) return null;
    var want = normalizeKey(key);
    for (var i = 0; i < surface.options.length; i++) {
      var opt = surface.options[i];
      var id = String(opt.id || '');
      if (!/^opt_size_/i.test(id)) continue;
      if (normalizeKey(id.replace(/^opt_size_/i, '')) === want) return opt;
    }
    return null;
  }

  function findSizeChoiceByKey(sizeOpt, sizeKey) {
    if (!sizeOpt || !sizeOpt.choices || !sizeKey) return null;
    var want = normalizeKey(sizeKey);
    for (var i = 0; i < sizeOpt.choices.length; i++) {
      var ch = sizeOpt.choices[i];
      if (normalizeKey(extractSizeKeyFromChoice(ch)) === want) return ch;
    }
    return null;
  }

  function isSizeAvailableForFrame(sizeKey, frameCode) {
    var sizeOpt = getSizeOptionForFrameCode(frameCode);
    if (!sizeOpt) return false;
    var ch = findSizeChoiceByKey(sizeOpt, sizeKey);
    return !!(ch && isChoiceEnabled(ch));
  }

  function getSelectedSizeKey() {
    if (state.values[SIZE_KEY_STATE]) return state.values[SIZE_KEY_STATE];
    var sizeOpt = findActiveSizeOption();
    if (sizeOpt) {
      var ch = getChoice(sizeOpt, state.values[sizeOpt.id]);
      if (ch) return extractSizeKeyFromChoice(ch);
    }
    return '';
  }

  function setSelectedSizeKey(sizeKey) {
    if (!sizeKey) return;
    state.values[SIZE_KEY_STATE] = sizeKey;
    getPerFrameSizeOptions().forEach(function (sizeOpt) {
      var ch = findSizeChoiceByKey(sizeOpt, sizeKey);
      state.values[sizeOpt.id] = ch ? ch.id : '';
    });
  }

  function getUnifiedSizeCatalog() {
    var map = {};
    getPerFrameSizeOptions().forEach(function (sizeOpt) {
      (sizeOpt.choices || []).forEach(function (ch) {
        if (!isChoiceEnabled(ch)) return;
        var key = extractSizeKeyFromChoice(ch);
        if (!key) return;
        if (!map[key]) {
          map[key] = { key: key, name: ch.name || key, order: ch.order != null ? ch.order : 9999 };
        } else if (ch.order != null && ch.order < map[key].order) {
          map[key].order = ch.order;
        }
      });
    });
    return Object.keys(map).map(function (k) { return map[k]; }).sort(function (a, b) {
      return (a.order || 0) - (b.order || 0) || a.key.localeCompare(b.key);
    });
  }

  function getActiveSizeChoice() {
    var frameOpt = findFrameOption();
    if (!frameOpt) return null;
    var fc = getFrameCodeFromChoice(getChoice(frameOpt, state.values[frameOpt.id]));
    var sizeOpt = getSizeOptionForFrameCode(fc);
    if (!sizeOpt) return null;
    var sizeKey = getSelectedSizeKey();
    if (sizeKey) return findSizeChoiceByKey(sizeOpt, sizeKey);
    return getChoice(sizeOpt, state.values[sizeOpt.id]);
  }

  function syncFrameForSelectedSize() {
    var frameOpt = findFrameOption();
    if (!frameOpt || !getSelectedSizeKey()) return;
    syncChoiceState(frameOpt);
  }

  function ensureDefaultSizeKey() {
    if (getSelectedSizeKey()) return;
    var catalog = getUnifiedSizeCatalog();
    if (catalog.length) setSelectedSizeKey(catalog[0].key);
  }

  function initPerFrameSizeMatrixState() {
    if (!usesPerFrameSizeMatrix()) return;
    var sizeOpt = findActiveSizeOption();
    if (sizeOpt) {
      var ch = getChoice(sizeOpt, state.values[sizeOpt.id]);
      if (ch) setSelectedSizeKey(extractSizeKeyFromChoice(ch));
    }
    ensureDefaultSizeKey();
    syncFrameForSelectedSize();
  }

  function getEnabledChoices(opt) {
    if (!opt || !opt.choices) return [];
    var choices = getCatalogChoices(opt);
    if (usesPerFrameSizeMatrix() && isFrameLikeChoiceOption(opt)) {
      var sizeKey = getSelectedSizeKey();
      if (sizeKey) {
        choices = choices.filter(function (ch) {
          return isSizeAvailableForFrame(sizeKey, getFrameCodeFromChoice(ch));
        });
      }
    }
    return choices;
  }

  function syncChoiceState(opt) {
    if (!opt || !opt.choices || !opt.choices.length) return;
    var enabled = getEnabledChoices(opt);
    if (!enabled.length) {
      state.values[opt.id] = '';
      return;
    }
    if (!getChoiceFromList(enabled, state.values[opt.id])) {
      var def = opt.defaultValue
        ? getChoiceFromList(enabled, opt.defaultValue)
        : null;
      state.values[opt.id] = def ? def.id : enabled[0].id;
    }
  }

  function isCardChoiceOption(opt) {
    if (!opt || !getCatalogChoices(opt).length) return false;
    if (opt.type === 'yes_no' || isFontOptionType(opt)) return false;
    if (opt.choiceDisplay === 'cards' || opt.displayAs === 'cards') return true;
    return (
      opt.type === 'frame_material' ||
      opt.type === 'choices' ||
      opt.type === 'dropdown' ||
      opt.type === 'single_select' ||
      opt.type === 'select'
    );
  }

  function isAddonChoiceOption(opt) {
    return isCardChoiceOption(opt) && !isFrameLikeChoiceOption(opt);
  }

  function isFrameLikeChoiceOption(opt) {
    if (!opt || isSizeOption(opt)) return false;
    if (opt.id === 'opt_frame') return true;
    if (opt.type === 'frame_material') return true;
    if (opt.role === 'frame' || opt.optionRole === 'frame') return true;
    if (opt.type === 'choices' || opt.type === 'single_select' || opt.type === 'select') {
      return (opt.choices || []).some(function (c) {
        return !!(c.previewUrl || c.mainUrl || c.priceDiff);
      });
    }
    return false;
  }

  function getColorChoicesForFont(opt, fontChoice) {
    if (fontChoice && fontChoice.colorChoices && fontChoice.colorChoices.length) {
      return fontChoice.colorChoices;
    }
    if (opt && opt.colorChoices && opt.colorChoices.length) return opt.colorChoices;
    return null;
  }

  function initFontColorDefault(opt, fontChoice) {
    var presets = getPresetColors(opt, fontChoice);
    if (!presets.length) return;
    var key = stateKeyColor(opt.id);
    var hexKey = stateKeyCustomHex(opt.id);
    if (state.values[key] === COLOR_CUSTOM_ID && state.values[hexKey]) return;
    if (getChoiceFromList(presets, state.values[key])) return;

    var def = presets.find(function (c) { return c.id === opt.defaultColorId; });
    if (!def && opt.textStyle) {
      var want = normalizeColorHex(getTextStyleBase(opt).fontColor);
      def = presets.find(function (c) { return normalizeColorHex(c.color || c.fontColor) === want; });
      if (!def && want) {
        state.values[key] = COLOR_CUSTOM_ID;
        state.values[hexKey] = want;
        return;
      }
    }
    state.values[key] = (def || presets[0]).id;
  }

  function initDefaultValues() {
    if (!surface || !surface.options) return;
    surface.options.forEach(function (opt) {
      if (opt.type === 'yes_no') {
        state.values[opt.id] = opt.defaultValue || 'no';
      } else if (isFontOptionType(opt)) {
        var fonts = getFontChoices(opt);
        var fdef = getChoiceFromList(fonts, opt.defaultValue) || fonts[0];
        state.values[opt.id] = fdef ? fdef.id : '';
        if (fdef) initFontColorDefault(opt, fdef);
      } else if (opt.type === 'font_color' || opt.type === 'text_color' || opt.type === 'color') {
        initFontColorDefault(opt, null);
      } else if (opt.type === 'image') {
        if (state.values[opt.id] == null) state.values[opt.id] = '';
        if (!state.values[stateKeyImageTransform(opt.id)]) {
          state.values[stateKeyImageTransform(opt.id)] = JSON.stringify(defaultImageTransform());
        }
      } else if (opt.type === 'text') {
        /* The confirm note never seeds its sampleText, so no placeholder
           signature is drawn on the preview before the customer types one. */
        state.values[opt.id] = isConfirmNoteOption(opt) ? '' : (opt.sampleText || '');
        var tFonts = getTextFontChoices(opt);
        if (tFonts.length) {
          var tFdef = getDefaultTextFontChoice(opt, tFonts);
          var curF = getChoiceFromList(tFonts, state.values[stateKeyFont(opt.id)]);
          if (!curF || !isRenderableFontChoice(curF)) {
            state.values[stateKeyFont(opt.id)] = tFdef ? tFdef.id : '';
            if (tFdef) {
              ensureFontChoiceLoaded(tFdef);
              initFontColorDefault(opt, tFdef);
            }
          } else {
            ensureFontChoiceLoaded(curF);
            initFontColorDefault(opt, curF);
          }
        } else if (opt.colorChoices && opt.colorChoices.length) {
          var tCols = opt.colorChoices;
          var tCdef = getChoiceFromList(tCols, opt.defaultColorId) || tCols[0];
          state.values[stateKeyColor(opt.id)] = tCdef ? tCdef.id : '';
        }
      } else if (isCardChoiceOption(opt)) {
        syncChoiceState(opt);
      }
    });
    initPerFrameSizeMatrixState();
  }

  function getOptionById(id) {
    if (!surface || !surface.options) return null;
    for (var i = 0; i < surface.options.length; i++) {
      if (surface.options[i].id === id) return surface.options[i];
    }
    return null;
  }

  function evalDisplayLogic(logic) {
    if (!logic || !logic.sourceOptionId) return true;
    var cur = state.values[logic.sourceOptionId];
    var expected = logic.value;
    var op = (logic.operator || 'equals').toLowerCase();
    if (op === 'equals' || op === 'eq') return String(cur) === String(expected);
    if (op === 'not_equals' || op === 'neq') return String(cur) !== String(expected);
    return String(cur) === String(expected);
  }

  function isOptionVisible(opt) {
    return evalDisplayLogic(opt.displayLogic);
  }

  function getChoice(opt, choiceId) {
    if (!opt) return null;
    if (opt.choices) return getChoiceFromList(opt.choices, choiceId);
    if (opt.fontChoices) return getChoiceFromList(opt.fontChoices, choiceId);
    if (opt.colorChoices) return getChoiceFromList(opt.colorChoices, choiceId);
    return null;
  }

  function findLinkedFontOption(textOptionId) {
    if (!surface || !surface.options || !textOptionId) return null;
    for (var i = 0; i < surface.options.length; i++) {
      var opt = surface.options[i];
      if (!isFontOptionType(opt)) continue;
      if (opt.linkedTextOptionId === textOptionId) return opt;
    }
    return null;
  }

  function findLinkedColorOption(textOptionId) {
    if (!surface || !surface.options || !textOptionId) return null;
    for (var i = 0; i < surface.options.length; i++) {
      var opt = surface.options[i];
      if (opt.type !== 'font_color' && opt.type !== 'text_color' && opt.type !== 'color') continue;
      if (opt.linkedTextOptionId === textOptionId) return opt;
    }
    return null;
  }

  function applyFontChoiceToStyle(style, fontChoice) {
    if (!fontChoice || !style) return;
    var customUrl = getCustomFontUrl(fontChoice);
    if (!isRenderableFont(fontChoice.fontFamily || fontChoice.name, customUrl, fontChoice.googleFontCssUrl)) return;
    if (fontChoice.fontFamily) style.fontFamily = fontChoice.fontFamily;
    if (fontChoice.cssFontFamily) style.cssFontFamily = fontChoice.cssFontFamily;
    if (customUrl) {
      style.customFontUrl = customUrl;
      style.googleFontCssUrl = '';
      loadCustomFontUrl(style.fontFamily, customUrl);
    } else if (fontChoice.googleFontCssUrl) {
      style.googleFontCssUrl = fontChoice.googleFontCssUrl;
      loadGoogleFontCssUrl(fontChoice.googleFontCssUrl);
    } else if (shouldLoadWebFont(fontChoice)) {
      loadGoogleFont(fontChoice.fontFamily);
    }
    if (fontChoice.fontBold != null) style.fontBold = fontChoice.fontBold;
    if (fontChoice.fontUnderline != null) style.fontUnderline = fontChoice.fontUnderline;
    if (fontChoice.fontSize) style.fontSize = fontChoice.fontSize;
    if (fontChoice.textCase) style.textCase = fontChoice.textCase;
  }

  function applyColorChoiceToStyle(style, colorChoice) {
    if (!colorChoice || !style) return;
    style.fontColor = colorChoice.color || colorChoice.fontColor || colorChoice.hex || style.fontColor;
  }

  function getEffectiveLines(opt) {
    if (!opt) return 1;
    var ts = opt.textStyle || {};
    var raw;
    if (opt.lines != null && opt.lines !== '') raw = opt.lines;
    else if (ts.lines != null && ts.lines !== '') raw = ts.lines;
    else raw = 1;
    var n = parseInt(raw, 10);
    if (isNaN(n) || n < 1) return 1;
    return n;
  }

  function applyTextLinesToElement(el, lines) {
    if (!el) return;
    var single = !lines || lines <= 1;
    el.classList.toggle('usipic-pcfg__text-el--single-line', single);
    el.classList.toggle('usipic-pcfg__text-el--multi-line', !single);
    if (single) {
      el.style.whiteSpace = 'nowrap';
      el.style.wordBreak = 'normal';
      el.style.webkitLineClamp = '';
      el.style.display = '';
      el.style.webkitBoxOrient = '';
    } else {
      el.style.whiteSpace = 'pre-wrap';
      el.style.wordBreak = 'break-word';
    }
  }
  function resolveAutoShrink(opt) {
    if (!opt) return true;
    var ts = opt.textStyle || {};
    if (ts.autoShrink === false || opt.autoShrink === false) return false;
    return true;
  }

  function getTextStyleBase(opt) {
    var ts = opt.textStyle || {};
    var family = ts.fontFamily || opt.fontFamily || 'Lobster';
    var customUrl = getCustomFontUrl(opt);
    var cssUrl = customUrl ? '' : ts.googleFontCssUrl || opt.googleFontCssUrl || buildGoogleFontCssUrl(family);
    return {
      autoShrink: resolveAutoShrink(opt),
      fontBold: ts.fontBold != null ? ts.fontBold : opt.fontBold,
      fontColor: ts.fontColor || opt.fontColor || '#594d3e',
      fontFamily: family,
      cssFontFamily: ts.cssFontFamily || opt.cssFontFamily || ('"' + family + '", Georgia, serif'),
      fontSize: ts.fontSize || opt.fontSize || 48,
      fontUnderline: ts.fontUnderline != null ? ts.fontUnderline : opt.fontUnderline,
      googleFontCssUrl: cssUrl,
      customFontUrl: customUrl,
      textCase: ts.textCase || opt.textCase || 'none',
      placement: ts.placement || opt.placement,
      lines: getEffectiveLines(opt),
    };
  }

  function resolveTextStyleForOption(opt) {
    var style = getTextStyleBase(opt);
    loadFontResource(style);

    var fontOpt = findLinkedFontOption(opt.id);
    if (fontOpt) {
      var fontCh = getChoiceFromList(getFontChoices(fontOpt), state.values[fontOpt.id]);
      if (fontCh && isRenderableFontChoice(fontCh)) {
        applyFontChoiceToStyle(style, fontCh);
        if (getPresetColors(fontOpt, fontCh).length) {
          applyColorChoiceToStyle(style, resolveSelectedColor(fontOpt.id, fontOpt, fontCh));
        }
      }
    } else {
      var inlineFonts = getTextFontChoices(opt);
      if (inlineFonts.length) {
        var inlineFont = getChoiceFromList(inlineFonts, state.values[stateKeyFont(opt.id)])
          || getDefaultTextFontChoice(opt, inlineFonts);
        if (inlineFont && isRenderableFontChoice(inlineFont)) {
          applyFontChoiceToStyle(style, inlineFont);
          if (getPresetColors(opt, inlineFont).length) {
            applyColorChoiceToStyle(style, resolveSelectedColor(opt.id, opt, inlineFont));
          }
        }
      }
    }

    var colorOpt = findLinkedColorOption(opt.id);
    if (colorOpt) {
      applyColorChoiceToStyle(style, resolveSelectedColor(colorOpt.id, colorOpt, null));
    } else if (!fontOpt && !getTextFontChoices(opt).length && getPresetColors(opt, null).length) {
      applyColorChoiceToStyle(style, resolveSelectedColor(opt.id, opt, null));
    }

    return style;
  }

  function tsHasGoogleUrl(style) {
    return !!(style && style.googleFontCssUrl);
  }

  function findFrameOption() {
    if (!surface || !surface.options) return null;
    for (var i = 0; i < surface.options.length; i++) {
      var opt = surface.options[i];
      if (isFrameLikeChoiceOption(opt) && isOptionVisible(opt)) return opt;
    }
    for (var j = 0; j < surface.options.length; j++) {
      if (isFrameLikeChoiceOption(surface.options[j])) return surface.options[j];
    }
    return null;
  }

  function resolveVariantForFrame(choice) {
    if (!choice || !boot.variants) return null;
    var cn = normalizeKey(choice.name);
    var frameCode = (choice.name || '').match(/F\d+/i);
    var code = frameCode ? frameCode[0].toLowerCase() : '';
    for (var i = 0; i < boot.variants.length; i++) {
      var v = boot.variants[i];
      var vt = normalizeKey(v.title);
      var vs = normalizeKey(v.sku);
      if (cn && (vt === cn || vs === cn)) return v;
      if (code && (vt.indexOf(code) >= 0 || vs.indexOf(code) >= 0)) return v;
      if (cn && vt.indexOf(cn) >= 0) return v;
    }
    return null;
  }

  /** API priceDiff is USD dollars (e.g. 40 — +$40.00) */
  function priceDiffCents(diff) {
    var n = Number(diff);
    if (!n || isNaN(n)) return 0;
    return Math.round(n * 100);
  }

  function getBaseVariant() {
    if (!boot.variants || !boot.variants.length) return null;
    var frameOpt = findFrameOption();
    if (frameOpt && frameOpt.choices) {
      var frameChoices = getEnabledChoices(frameOpt);
      for (var i = 0; i < frameChoices.length; i++) {
        var ch = frameChoices[i];
        if (!ch.priceDiff) {
          var v = resolveVariantForFrame(ch);
          if (v) return v;
        }
      }
    }
    return boot.variants.find(function (v) { return v.available; }) || boot.variants[0];
  }

  function getDisplayPriceCents(choice) {
    var matched = resolveVariantForFrame(choice);
    if (matched) return matched.price;
    var base = getBaseVariant();
    if (!base) return 0;
    return base.price + priceDiffCents(choice && choice.priceDiff);
  }

  function getDisplayCompareCents(choice, priceCents) {
    var matched = resolveVariantForFrame(choice);
    if (matched && matched.compare_at_price > matched.price) return matched.compare_at_price;
    var base = getBaseVariant();
    if (!base || !base.compare_at_price || base.compare_at_price <= base.price) return 0;
    var addon = priceDiffCents(choice && choice.priceDiff);
    var compare = base.compare_at_price + addon;
    return compare > priceCents ? compare : 0;
  }

  /* Frame code → material label, from snippets/usipic-frame-materials.liquid.
     Returns '' when the code is not in the table, so the card just shows less. */
  function frameMaterialLabel(code) {
    var map = (boot && boot.frameMaterials) || {};
    var entry = map[String(code || '').trim().toUpperCase()];
    if (!entry) return '';
    var parts = [];
    if (entry.material) parts.push(entry.material);
    if (entry.detail) parts.push(entry.detail);
    return parts.join(' ');
  }

  function frameChoicePriceLabel(ch) {
    if (!ch) return '';
    var base = getBaseVariant();
    if (!base) return '';
    if (usesPerFrameSizeMatrix()) {
      var fc = getFrameCodeFromChoice(ch);
      var sizeKey = getSelectedSizeKey();
      if (fc && sizeKey) {
        var sizeOpt = getSizeOptionForFrameCode(fc);
        var sizeCh = sizeOpt ? findSizeChoiceByKey(sizeOpt, sizeKey) : null;
        if (sizeCh && sizeCh.priceDiff) {
          return '+' + formatMoney(priceDiffCents(sizeCh.priceDiff));
        }
      }
    }
    var matched = resolveVariantForFrame(ch);
    if (matched && matched.price > base.price) {
      return '+' + formatMoney(matched.price - base.price);
    }
    if (ch.priceDiff) return '+' + formatMoney(priceDiffCents(ch.priceDiff));
    return '';
  }

  function getNonFrameAddonCents() {
    var total = 0;
    if (!surface || !surface.options) return total;
    surface.options.forEach(function (opt) {
      if (!isAddonChoiceOption(opt) || isFrameLikeChoiceOption(opt) || isAggregatedSizeOption(opt)) return;
      if (!isOptionVisible(opt)) return;
      var ch = getChoice(opt, state.values[opt.id]);
      if (ch && ch.priceDiff) total += priceDiffCents(ch.priceDiff);
    });
    return total;
  }

  function getSelectedAddonCents() {
    return getNonFrameAddonCents();
  }

  /** Build the shared I'PIC matrix SKU: CP0000 + model + zero-padded W + H (e.g. CP0000F933024). */
  function buildIpicSku(sizeKey, model) {
    if (!sizeKey || !model) return '';
    var parts = String(sizeKey).replace(/\s+/g, '').toLowerCase().split('x');
    if (parts.length !== 2) return '';
    var w = parseInt(parts[0], 10);
    var h = parseInt(parts[1], 10);
    if (isNaN(w) || isNaN(h)) return '';
    var pad = function (n) { return n < 10 ? '0' + n : String(n); };
    return 'CP0000' + String(model).toUpperCase().trim() + pad(w) + pad(h);
  }

  /** Look up a pre-priced size x model variant from the shared I'PIC matrix product. */
  function findIpicVariantBySku(sku) {
    if (!sku) return null;
    var list = (boot && boot.ipicVariants) || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].sku || '').toUpperCase() === sku) return list[i];
    }
    return null;
  }

  function pickActivePricing() {
    var base = getBaseVariant();
    var baseCents = base ? base.price : 0;
    var extraCents = getNonFrameAddonCents();
    var frameOpt = findFrameOption();

    if (!frameOpt) {
      var v = findIpicVariantBySku(buildIpicSku(getSelectedSizeKey(), 'C25'))
        || base || (boot.variants && boot.variants[0]);
      var basePrice = v ? v.price : 0;
      state.variantId = v ? v.id : null;
      return {
        variant: v,
        priceCents: basePrice + extraCents,
        compareCents: v && v.compare_at_price > v.price ? v.compare_at_price + extraCents : 0,
        addonCents: extraCents,
      };
    }

    var choice = getChoice(frameOpt, state.values[frameOpt.id]);
    var matched = resolveVariantForFrame(choice);
    var matrixSizeCh = null;
    var priceCents = baseCents;

    if (usesPerFrameSizeMatrix()) {
      matrixSizeCh = getActiveSizeChoice();
      if (matrixSizeCh && matrixSizeCh.priceDiff != null && !isNaN(Number(matrixSizeCh.priceDiff))) {
        priceCents = baseCents + priceDiffCents(matrixSizeCh.priceDiff);
      } else if (matched) {
        priceCents = matched.price;
      } else {
        priceCents = getDisplayPriceCents(choice);
      }
    } else {
      if (choice && choice.priceDiff != null && !isNaN(Number(choice.priceDiff))) {
        priceCents = baseCents + priceDiffCents(choice.priceDiff);
      } else {
        priceCents = getDisplayPriceCents(choice);
      }
    }

    priceCents += extraCents;
    /* This product only has a base-price variant, so the cart line must carry the
       matrix variant for the selected size + model (otherwise the add-ons are never charged). */
    var ipicVariant = findIpicVariantBySku(buildIpicSku(getSelectedSizeKey(), getFrameCodeFromChoice(choice)));
    var cartVariant = ipicVariant || matched || base;
    state.variantId = cartVariant ? cartVariant.id : null;
    return {
      variant: cartVariant,
      priceCents: priceCents,
      compareCents: getDisplayCompareCents(choice, priceCents - extraCents) + extraCents,
      choice: choice,
      matrixSizeCh: matrixSizeCh,
      usesPriceDiff: !!(usesPerFrameSizeMatrix()
        ? (matrixSizeCh && matrixSizeCh.priceDiff && !matched)
        : (choice && choice.priceDiff && !matched)),
      addonCents: priceCents - baseCents,
    };
  }

  function pickActiveVariant() {
    return pickActivePricing().variant;
  }

  function updatePriceUI(pricing) {
    if (!pricing) pricing = pickActivePricing();
    var variant = pricing.variant;
    var priceEl = qs('#usipic-pcfg-price');
    var compareEl = qs('#usipic-pcfg-compare');
    if (!priceEl) return;
    priceEl.textContent = formatMoney(pricing.priceCents);
    if (compareEl) {
      if (pricing.compareCents && pricing.compareCents > pricing.priceCents) {
        compareEl.textContent = formatMoney(pricing.compareCents);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    var atc = qs('#usipic-pcfg-atc');
    if (atc) {
      var imgCheck = validateRequiredImages();
      var canBuy = variant && variant.available && imgCheck.ok;
      atc.disabled = !canBuy;
      /* Update the inner label only, so the button icon is not wiped. */
      var atcLabel = qs('#usipic-pcfg-atc-label');
      (atcLabel || atc).textContent = variant && variant.available ? (boot.btnAtc || 'Add to cart') : (boot.btnSoldOut || 'Sold out');
    }
  }

  /** Map / product base art — never replaced by frame choice thumbnails */
  function getBasePreviewUrl() {
    if (surface && surface.previewImageUrl) return surface.previewImageUrl;
    if (boot.media && boot.media.length) {
      var m = boot.media.find(function (x) { return x.type === 'image' && x.src; });
      if (m) return m.src;
    }
    return '';
  }

  function getFrameOverlayUrl() {
    if (!surface || !surface.options) return '';
    for (var i = 0; i < surface.options.length; i++) {
      var opt = surface.options[i];
      if (!isFrameLikeChoiceOption(opt) || !isOptionVisible(opt)) continue;
      var choice = getChoice(opt, state.values[opt.id]);
      if (choice && choice.previewUrl) return choice.previewUrl;
      if (choice && choice.mainUrl) return choice.mainUrl;
    }
    return '';
  }

  function findYesNoOption() {
    if (!surface || !surface.options) return null;
    for (var i = 0; i < surface.options.length; i++) {
      if (surface.options[i].type === 'yes_no') return surface.options[i];
    }
    return null;
  }

  /** yes.previewUrl — full canvas (100% × 100%, top-left) */
  function getTitleCoverUrl() {
    var yn = findYesNoOption();
    if (!yn || state.values[yn.id] !== 'yes') return '';
    var yesChoice = null;
    (yn.choices || []).forEach(function (ch) {
      if (ch.id === 'yes') yesChoice = ch;
    });
    return yesChoice && yesChoice.previewUrl ? yesChoice.previewUrl : '';
  }

  function syncDesignStageMetrics() {
    var stage = qs('#usipic-pcfg-stage');
    if (!stage || !surface) return;
    var dw = surface.designWidth || 1200;
    var dh = surface.designHeight || 1200;
    stage.style.setProperty('--pcfg-design-w', String(dw));
    stage.style.setProperty('--pcfg-design-h', String(dh));
    stage.style.aspectRatio = dw + ' / ' + dh;
  }

  function getActiveThumbIndex() {
    var stage = qs('#usipic-pcfg-stage');
    if (!stage || stage.dataset.activeThumbIndex == null) return 0;
    var n = parseInt(stage.dataset.activeThumbIndex, 10);
    return isNaN(n) ? 0 : n;
  }

  function syncThumbUi(idx) {
    var wrap = qs('#usipic-pcfg-thumbs');
    if (!wrap) return;
    wrap.querySelectorAll('.usipic-pcfg__thumb').forEach(function (t) {
      var i = parseInt(t.dataset.thumbIndex, 10);
      var on = i === idx;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    setActiveThumbIndex(idx);
    updateGalleryControls();
  }

  function getThumbWindowStart(itemsLen, activeIdx) {
    if (itemsLen <= THUMB_VISIBLE_MAX) return 0;
    var start = activeIdx - 1;
    if (start < 0) start = 0;
    if (start > itemsLen - THUMB_VISIBLE_MAX) start = itemsLen - THUMB_VISIBLE_MAX;
    return start;
  }

  function renderVisibleThumbs(force) {
    var wrap = qs('#usipic-pcfg-thumbs');
    if (!wrap) return;
    var items = getThumbItemsList();
    var activeIdx = getActiveThumbIndex();
    if (activeIdx >= items.length) activeIdx = 0;
    var start = getThumbWindowStart(items.length, activeIdx);
    var windowKey = items.map(function (i) { return i.src; }).join('|') + '@' + start;
    if (!force && windowKey === _thumbItemsKey && wrap.children.length) {
      syncThumbUi(activeIdx);
      return;
    }
    _thumbItemsKey = windowKey;
    wrap.innerHTML = '';

    var end = Math.min(start + THUMB_VISIBLE_MAX, items.length);
    for (var idx = start; idx < end; idx++) {
      var item = items[idx];
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'usipic-pcfg__thumb' + (idx === activeIdx ? ' is-active' : '');
      btn.dataset.thumbIndex = String(idx);
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', idx === activeIdx ? 'true' : 'false');
      btn.innerHTML = '<img src="' + escapeHtml(item.thumb || item.src) + '" alt="' + escapeHtml(item.alt || '') + '" loading="eager" decoding="async">';
      btn.addEventListener('click', function () {
        activateThumb(parseInt(this.dataset.thumbIndex, 10));
      });
      wrap.appendChild(btn);
    }

    syncThumbUi(activeIdx);
  }

  function updateGalleryControls() {
    var items = getThumbItemsList();
    var total = items.length;
    var idx = getActiveThumbIndex();
    var zoom = qs('#usipic-pcfg-gallery-zoom');
    /* First thumb = live design preview — no zoom */
    if (zoom) zoom.hidden = total < 1 || idx === 0;
    if (idx >= total && total > 0) syncThumbUi(0);
  }

  function activateThumb(idx) {
    var items = getThumbItemsList();
    if (!items.length) return;
    if (idx < 0) idx = items.length - 1;
    if (idx >= items.length) idx = 0;
    setActiveThumbIndex(idx);
    if (idx === 0) {
      setPreviewImage(items[idx].src, { runOverlays: true });
    } else {
      hidePreviewOverlays();
      setPreviewImage(items[idx].src, { runOverlays: false });
    }
    renderVisibleThumbs(false);
  }

  function getCurrentPreviewFullUrl() {
    var items = getThumbItemsList();
    var idx = getActiveThumbIndex();
    if (!items.length) return '';
    var item = items[idx] || items[0];
    return resolvePreviewImageUrl(item.src || '');
  }

  function bindGalleryControls() {
    var zoom = qs('#usipic-pcfg-gallery-zoom');
    if (zoom && !zoom.dataset.bound) {
      zoom.dataset.bound = '1';
      zoom.addEventListener('click', function () {
        if (getActiveThumbIndex() === 0) return;
        var url = getCurrentPreviewFullUrl();
        if (!url) return;
        window.open(url, '_blank', 'noopener,noreferrer');
      });
    }
  }

  function isPlainPreviewActive() {
    return getActiveThumbIndex() !== 0;
  }

  function ensureDesignPreviewActive() {
    if (!isPlainPreviewActive()) return;
    syncThumbUi(0);
    var baseUrl = getBasePreviewUrl();
    if (baseUrl) setPreviewImage(baseUrl);
  }

  function refreshDesignPreview() {
    ensureDesignPreviewActive();
    updateCoverOverlays();
    renderImageLayers();
    renderTextOverlays();
  }

  function resolvePreviewImageUrl(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) return 'https:' + url;
    try {
      return new URL(url, window.location.href).href;
    } catch (e) {
      return url;
    }
  }

  function getDisplayPreviewUrl(url) {
    var resolved = resolvePreviewImageUrl(url);
    if (!resolved || resolved.indexOf('cdn.shopify.com') === -1) return resolved;
    try {
      var u = new URL(resolved);
      u.searchParams.set('width', '900');
      return u.href;
    } catch (e) {
      return resolved;
    }
  }

  function preloadPreviewImage(url, useCrossOrigin) {
    var resolved = getDisplayPreviewUrl(url);
    if (!resolved) return null;
    if (_previewImageCache[resolved]) return _previewImageCache[resolved];
    var im = new Image();
    im.decoding = 'async';
    if (useCrossOrigin) im.crossOrigin = 'anonymous';
    im.src = resolved;
    _previewImageCache[resolved] = im;
    return im;
  }

  function preloadThumbItems(items) {
    (items || []).forEach(function (item, idx) {
      preloadPreviewImage(item.src, idx === 0);
    });
  }

  function runPreviewOverlaysIfNeeded() {
    if (isPlainPreviewActive()) return;
    updateCoverOverlays();
    renderImageLayers();
    renderTextOverlays();
  }

  function hidePreviewOverlays() {
    var titleCover = qs('#usipic-pcfg-title-cover');
    var frameOverlay = qs('#usipic-pcfg-frame-overlay');
    var layer = qs('#usipic-pcfg-text-layer');
    var imageLayer = qs('#usipic-pcfg-image-layer');
    var handlesLayer = qs('#usipic-pcfg-image-handles-layer');
    if (titleCover) {
      titleCover.hidden = true;
      titleCover.onload = null;
      titleCover.removeAttribute('src');
    }
    if (frameOverlay) {
      frameOverlay.hidden = true;
      frameOverlay.removeAttribute('src');
    }
    if (layer) layer.innerHTML = '';
    if (imageLayer) imageLayer.innerHTML = '';
    if (handlesLayer) handlesLayer.innerHTML = '';
  }

  function updateCoverOverlays() {
    if (isPlainPreviewActive()) {
      hidePreviewOverlays();
      return;
    }
    var titleCover = qs('#usipic-pcfg-title-cover');
    var frameOverlay = qs('#usipic-pcfg-frame-overlay');
    var titleCoverUrl = getTitleCoverUrl();

    if (titleCover) {
      if (titleCoverUrl) {
        titleCover.hidden = false;
        if (titleCover.getAttribute('src') !== titleCoverUrl) {
          titleCover.crossOrigin = 'anonymous';
          titleCover.onload = function () {
            titleCover.onload = null;
            renderTextOverlays();
          };
          titleCover.src = titleCoverUrl;
        }
      } else {
        titleCover.hidden = true;
        titleCover.onload = null;
        titleCover.removeAttribute('src');
      }
    }

    if (frameOverlay) {
      var frameUrl = getFrameOverlayUrl();
      if (frameUrl) {
        frameOverlay.hidden = false;
        if (frameOverlay.getAttribute('src') !== frameUrl) {
          frameOverlay.crossOrigin = 'anonymous';
          frameOverlay.src = frameUrl;
        }
      } else {
        frameOverlay.hidden = true;
        frameOverlay.removeAttribute('src');
      }
    }
    renderImageLayers();
  }

  function loadGoogleFontCssUrl(url) {
    if (!url || loadedFonts[url]) return;
    loadedFonts[url] = true;
    var id = 'usipic-pcfg-font-' + String(url).replace(/[^a-z0-9]+/gi, '-').slice(0, 80);
    if (document.getElementById(id)) return;
    var link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  }

  function loadGoogleFont(family) {
    if (!family || loadedFonts[family]) return;
    loadedFonts[family] = true;
    loadGoogleFontCssUrl(
      'https://fonts.googleapis.com/css2?family=' +
        encodeURIComponent(family) +
        ':wght@400;700&display=swap'
    );
  }

  function loadGoogleFontsBatch(names) {
    var clean = (names || []).filter(Boolean);
    if (!clean.length) return;
    var batchKey = 'batch:' + clean.join('|');
    if (loadedFonts[batchKey]) return;
    loadedFonts[batchKey] = true;
    var qs = clean
      .map(function (n) {
        return 'family=' + encodeURIComponent(n).replace(/%20/g, '+') + ':wght@400;700';
      })
      .join('&');
    loadGoogleFontCssUrl('https://fonts.googleapis.com/css2?' + qs + '&display=swap');
    clean.forEach(function (n) {
      loadedFonts[n] = true;
    });
  }

  function preloadFontsForMenu(fonts) {
    var names = fonts.map(function (ch) { return ch.fontFamily; }).filter(Boolean);
    var chunk = 12;
    for (var i = 0; i < names.length; i += chunk) {
      loadGoogleFontsBatch(names.slice(i, i + chunk));
    }
  }

  function applyFontPreview(el, ch) {
    if (!el || !ch) return;
    var family = fontChoiceCssFamily(ch);
    el.style.setProperty('font-family', family, 'important');
    el.style.setProperty('--pcfg-font-preview', family);
    el.dataset.fontFamily = ch.fontFamily || '';
  }

  function applyFontPreviewWhenReady(el, ch) {
    if (!el || !ch) return;
    ensureFontChoiceLoaded(ch);
    var family = fontChoiceCssFamily(ch);
    function paint() {
      applyFontPreview(el, ch);
    }
    if (document.fonts && document.fonts.load) {
      document.fonts.load('20px ' + family).then(paint).catch(paint);
    } else {
      paint();
    }
  }

  function observeFontPreview(el, ch) {
    if (!el || !ch || !ch.fontFamily) return;
    ensureFontChoiceLoaded(ch);
    if (typeof IntersectionObserver === 'undefined') return;
    var io = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        ensureFontChoiceLoaded(ch);
        if (document.fonts && document.fonts.load) {
          document.fonts.load('16px ' + fontChoiceCssFamily(ch)).then(function () {
            applyFontPreviewWhenReady(el, ch);
          });
        }
        observer.unobserve(entry.target);
      });
    }, { root: null, threshold: 0.01 });
    io.observe(el);
  }

  function preloadConfigFonts() {
    if (!surface || !surface.options) return;
    surface.options.forEach(function (opt) {
      var customUrl = getCustomFontUrl(opt);
      var family = (opt.textStyle && opt.textStyle.fontFamily) || opt.fontFamily;
      if (customUrl && family) loadCustomFontUrl(family, customUrl);
      loadFontResource(fontChoiceFromTextStyle(opt));
      getFontChoices(opt).forEach(function (ch) {
        loadFontResource(ch);
      });
    });
  }

  function toColorInputValue(hex) {
    var n = normalizeColorHex(hex);
    return n && n.charAt(0) === '#' && n.length === 7 ? n : '#111111';
  }

  function appendColorPicker(container, optId, opt, fontChoice, onSelect) {
    var presets = getPresetColors(opt, fontChoice);
    if (!presets.length) return;

    var row = document.createElement('div');
    row.className = 'usipic-pcfg__color-row';
    var selectedId = state.values[stateKeyColor(optId)];
    var customHex = state.values[stateKeyCustomHex(optId)] || toColorInputValue(getTextStyleBase(opt || {}).fontColor);

    function setSelected(target) {
      row.querySelectorAll('.usipic-pcfg__color-swatch, .usipic-pcfg__color-custom').forEach(function (el) {
        el.classList.remove('is-selected');
      });
      if (target) target.classList.add('is-selected');
    }

    function applyColorChoice(ch) {
      if (onSelect) onSelect(ch);
      refreshDesignPreview();
    }

    presets.forEach(function (ch) {
      var hex = ch.color || ch.fontColor || ch.hex || '#111111';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'usipic-pcfg__color-swatch' + (selectedId === ch.id ? ' is-selected' : '');
      btn.style.setProperty('--pcfg-swatch', hex);
      btn.style.backgroundColor = hex;
      btn.setAttribute('aria-label', ch.name || hex);
      btn.addEventListener('click', function () {
        state.values[stateKeyColor(optId)] = ch.id;
        delete state.values[stateKeyCustomHex(optId)];
        setSelected(btn);
        applyColorChoice(ch);
      });
      row.appendChild(btn);
    });

    var customWrap = document.createElement('label');
    customWrap.className = 'usipic-pcfg__color-custom' + (selectedId === COLOR_CUSTOM_ID ? ' is-selected' : '');

    var colorInput = document.createElement('input');
    colorInput.type = 'color';
    colorInput.className = 'usipic-pcfg__color-input';
    colorInput.value = toColorInputValue(customHex);
    colorInput.setAttribute('aria-label', 'Custom color');

    function paintCustomWrap() {
      customWrap.style.setProperty('--pcfg-swatch-custom', colorInput.value);
      customWrap.style.backgroundColor = colorInput.value;
    }

    function commitCustomColor() {
      state.values[stateKeyColor(optId)] = COLOR_CUSTOM_ID;
      state.values[stateKeyCustomHex(optId)] = colorInput.value;
      paintCustomWrap();
      setSelected(customWrap);
      applyColorChoice({ id: COLOR_CUSTOM_ID, name: 'Custom', color: colorInput.value });
    }

    colorInput.addEventListener('input', commitCustomColor);
    colorInput.addEventListener('change', commitCustomColor);

    if (selectedId === COLOR_CUSTOM_ID) paintCustomWrap();

    customWrap.appendChild(colorInput);
    var customIcon = document.createElement('span');
    customIcon.className = 'usipic-pcfg__color-custom-icon';
    customIcon.setAttribute('aria-hidden', 'true');
    customIcon.textContent = '+';
    customWrap.appendChild(customIcon);
    row.appendChild(customWrap);

    container.appendChild(row);
  }

  function fontChoiceCssFamily(ch) {
    if (!ch) return 'inherit';
    return ch.cssFontFamily || (ch.fontFamily ? '"' + ch.fontFamily + '", sans-serif' : 'inherit');
  }

  function ensureFontChoiceLoaded(ch) {
    if (!ch || !isRenderableFontChoice(ch)) return;
    loadFontResource(ch);
  }

  function bindFontMenuOutsideClick() {
    if (fontMenuOutsideBound) return;
    fontMenuOutsideBound = true;
    document.addEventListener('click', function (e) {
      document.querySelectorAll('.usipic-pcfg__font-menu:not([hidden])').forEach(function (menu) {
        var wrap = menu.closest('.usipic-pcfg__font-select');
        if (wrap && wrap.contains(e.target)) return;
        menu.hidden = true;
        var toggle = wrap && wrap.querySelector('.usipic-pcfg__font-toggle');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    });
  }

  function appendFontDropdown(container, stateKey, fonts, onSelect, opt) {
    if (!fonts || !fonts.length) return;

    loadGoogleFontsBatch(fonts.slice(0, 12).map(function (ch) { return ch.fontFamily; }));

    var wrap = document.createElement('div');
    wrap.className = 'usipic-pcfg__font-select';

    var selected = getChoiceFromList(fonts, state.values[stateKey])
      || (opt ? getDefaultTextFontChoice(opt, fonts) : null)
      || fonts[0];

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'usipic-pcfg__font-toggle';
    toggle.setAttribute('aria-haspopup', 'listbox');
    toggle.setAttribute('aria-expanded', 'false');

    var menu = document.createElement('div');
    menu.className = 'usipic-pcfg__font-menu';
    menu.hidden = true;
    menu.setAttribute('role', 'listbox');

    var search = document.createElement('input');
    search.type = 'text';
    search.className = 'usipic-pcfg__font-search';
    search.placeholder = 'Search fonts';
    search.setAttribute('aria-label', 'Search fonts');
    search.addEventListener('click', function (e) { e.stopPropagation(); });
    search.addEventListener('keydown', function (e) { e.stopPropagation(); });
    search.addEventListener('input', function () {
      var q = search.value.toLowerCase().trim();
      menu.querySelectorAll('.usipic-pcfg__font-opt').forEach(function (btn) {
        var label = (btn.getAttribute('data-font-label') || btn.textContent || '').toLowerCase();
        btn.hidden = !!(q && label.indexOf(q) < 0);
      });
    });
    menu.appendChild(search);

    var toggleLabel = document.createElement('span');
    toggleLabel.className = 'usipic-pcfg__font-preview';
    toggle.appendChild(toggleLabel);

    function syncToggle(ch) {
      toggleLabel.textContent = ch.name || ch.fontFamily || ch.id;
      applyFontPreviewWhenReady(toggleLabel, ch);
    }

    function closeMenu() {
      menu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      search.value = '';
      menu.querySelectorAll('.usipic-pcfg__font-opt').forEach(function (btn) {
        btn.hidden = false;
      });
    }

    fonts.forEach(function (ch) {
      var optBtn = document.createElement('button');
      optBtn.type = 'button';
      optBtn.className = 'usipic-pcfg__font-opt';
      optBtn.setAttribute('role', 'option');
      optBtn.setAttribute('data-font-label', ch.name || ch.fontFamily || ch.id);
      var optLabel = document.createElement('span');
      optLabel.className = 'usipic-pcfg__font-preview';
      optLabel.textContent = ch.name || ch.fontFamily || ch.id;
      applyFontPreviewWhenReady(optLabel, ch);
      observeFontPreview(optLabel, ch);
      optBtn.appendChild(optLabel);
      if (selected && selected.id === ch.id) optBtn.setAttribute('aria-selected', 'true');
      optBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        state.values[stateKey] = ch.id;
        menu.querySelectorAll('.usipic-pcfg__font-opt').forEach(function (b) {
          b.setAttribute('aria-selected', 'false');
        });
        optBtn.setAttribute('aria-selected', 'true');
        syncToggle(ch);
        closeMenu();
        if (onSelect) onSelect(ch);
      });
      menu.appendChild(optBtn);
    });

    toggle.addEventListener('click', function (e) {
      e.stopPropagation();
      if (!menu.hidden) {
        closeMenu();
        return;
      }
      preloadFontsForMenu(fonts);
      menu.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          menu.querySelectorAll('.usipic-pcfg__font-preview').forEach(function (el) {
            var ch = findFontChoiceByFamily(fonts, el.dataset.fontFamily);
            if (ch) applyFontPreviewWhenReady(el, ch);
          });
        });
      }
      search.focus();
    });

    syncToggle(selected);
    wrap.appendChild(toggle);
    wrap.appendChild(menu);
    container.appendChild(wrap);
  }

  function appendFontColorRow(parent, fontStateKey, opt, fonts) {
    if (!fonts || !fonts.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'usipic-pcfg__font-color';
    var row = document.createElement('div');
    row.className = 'usipic-pcfg__style-row';

    var fontCol = document.createElement('div');
    fontCol.className = 'usipic-pcfg__style-col usipic-pcfg__style-col--font';
    var fontLabel = document.createElement('p');
    fontLabel.className = 'usipic-pcfg__sub-label';
    fontLabel.textContent = 'Font';
    fontCol.appendChild(fontLabel);
    appendFontDropdown(fontCol, fontStateKey, fonts, function (ch) {
      initFontColorDefault(opt, ch);
      refreshDesignPreview();
    }, opt);
    row.appendChild(fontCol);

    var fontCh = getChoiceFromList(fonts, state.values[fontStateKey]);
    if (getPresetColors(opt, fontCh).length) {
      var colorCol = document.createElement('div');
      colorCol.className = 'usipic-pcfg__style-col usipic-pcfg__style-col--color';
      var colorLabel = document.createElement('p');
      colorLabel.className = 'usipic-pcfg__sub-label';
      colorLabel.textContent = 'Color';
      colorCol.appendChild(colorLabel);
      appendColorPicker(colorCol, opt.id, opt, fontCh, null);
      row.appendChild(colorCol);
    }

    wrap.appendChild(row);
    parent.appendChild(wrap);
  }

  function appendTextInputFontColorRow(block, opt, textFonts) {
    var fontKey = stateKeyFont(opt.id);
    var textFontDefault = getDefaultTextFontChoice(opt, textFonts);
    var curFont = getChoiceFromList(textFonts, state.values[fontKey]);
    if (!curFont || !isRenderableFontChoice(curFont)) {
      state.values[fontKey] = textFontDefault ? textFontDefault.id : '';
    } else if (!state.values[fontKey] && textFontDefault) {
      state.values[fontKey] = textFontDefault.id;
    }

    var wrap = document.createElement('div');
    wrap.className = 'usipic-pcfg__font-color';
    var row = document.createElement('div');
    row.className = 'usipic-pcfg__style-row usipic-pcfg__style-row--text-inline';

    var textCol = document.createElement('div');
    textCol.className = 'usipic-pcfg__style-col usipic-pcfg__style-col--text';
    var inp = document.createElement(getEffectiveLines(opt) > 1 ? 'textarea' : 'input');
    inp.className = 'usipic-pcfg__field';
    if (getEffectiveLines(opt) > 1) inp.rows = getEffectiveLines(opt);
    else inp.type = 'text';
    inp.value = state.values[opt.id] || '';
    inp.placeholder = opt.sampleText || '';
    if (opt.maxChars) inp.maxLength = opt.maxChars;
    inp.addEventListener('input', function () {
      state.values[opt.id] = inp.value;
      refreshDesignPreview();
    });
    textCol.appendChild(inp);
    row.appendChild(textCol);

    var fontCol = document.createElement('div');
    fontCol.className = 'usipic-pcfg__style-col usipic-pcfg__style-col--font';
    var fontLabel = document.createElement('p');
    fontLabel.className = 'usipic-pcfg__sub-label';
    fontLabel.textContent = 'Font';
    fontCol.appendChild(fontLabel);
    appendFontDropdown(fontCol, fontKey, textFonts, function (ch) {
      initFontColorDefault(opt, ch);
      refreshDesignPreview();
    }, opt);
    row.appendChild(fontCol);

    var fontCh = getChoiceFromList(textFonts, state.values[fontKey]);
    if (getPresetColors(opt, fontCh).length) {
      var colorCol = document.createElement('div');
      colorCol.className = 'usipic-pcfg__style-col usipic-pcfg__style-col--color';
      var colorLabel = document.createElement('p');
      colorLabel.className = 'usipic-pcfg__sub-label';
      colorLabel.textContent = 'Color';
      colorCol.appendChild(colorLabel);
      appendColorPicker(colorCol, opt.id, opt, fontCh, null);
      row.appendChild(colorCol);
    }

    wrap.appendChild(row);
    block.appendChild(wrap);
  }

  function renderFontColorPickers(block, opt) {
    appendFontColorRow(block, opt.id, opt, getFontChoices(opt));
  }

  function getTextFitTarget(el) {
    return el ? el.querySelector('.usipic-pcfg__text-el-inner') || el : el;
  }

  function textOverflowsBox(el, singleLine) {
    if (!el) return false;
    var target = getTextFitTarget(el);
    var widthOverflow = target.scrollWidth > el.clientWidth + 1;
    var heightOverflow = target.scrollHeight > el.clientHeight + 1;
    return widthOverflow || heightOverflow;
  }

  function fitTextInBox(el, maxPx) {
    if (!el) return;
    var fs = maxPx;
    el.style.fontSize = fs + 'px';
    var target = getTextFitTarget(el);
    if (target !== el) target.style.fontSize = fs + 'px';
    var guard = 0;
    while (guard < 120 && fs > 6 && textOverflowsBox(el, true)) {
      fs -= 1;
      el.style.fontSize = fs + 'px';
      if (target !== el) target.style.fontSize = fs + 'px';
      guard++;
    }
  }

  function renderTextOverlays() {
    var layer = qs('#usipic-pcfg-text-layer');
    var stage = qs('#usipic-pcfg-stage');
    if (!layer || !stage || !surface) return;

    if (isPlainPreviewActive()) {
      layer.innerHTML = '';
      return;
    }

    syncDesignStageMetrics();

    var dw = surface.designWidth || 1200;
    var dh = surface.designHeight || 1200;

    layer.innerHTML = '';
    if (!surface.options) return;

    surface.options.forEach(function (opt) {
      if (opt.type !== 'text' || !isOptionVisible(opt)) return;
      var text = state.values[opt.id];
      if (!text || !String(text).trim()) return;

      var style = resolveTextStyleForOption(opt);
      var p = style.placement;
      if (!p) return;

      var displayText = String(text);
      if ((style.lines || 1) <= 1) {
        displayText = displayText.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
      }

      var el = document.createElement('div');
      el.className = 'usipic-pcfg__text-el';
      var inner = document.createElement('span');
      inner.className = 'usipic-pcfg__text-el-inner';
      inner.textContent = displayText;
      el.appendChild(inner);
      applyTextLinesToElement(el, style.lines);
      el.style.left = (100 * p.x / dw) + '%';
      el.style.top = (100 * p.y / dh) + '%';
      el.style.width = (100 * p.width / dw) + '%';
      el.style.height = (100 * p.height / dh) + '%';
      el.style.color = style.fontColor || '#594d3e';
      el.style.fontFamily = style.cssFontFamily || ('"' + (style.fontFamily || 'Lobster') + '", Georgia, serif');
      el.style.fontWeight = style.fontBold ? '700' : '400';
      el.style.textDecoration = style.fontUnderline ? 'underline' : 'none';
      if (style.textCase === 'uppercase') el.style.textTransform = 'uppercase';
      else if (style.textCase === 'lowercase') el.style.textTransform = 'lowercase';

      var baseFs = style.fontSize || 48;
      var cssFamily = style.cssFontFamily || ('"' + (style.fontFamily || 'Lobster') + '", Georgia, serif');
      layer.appendChild(el);

      requestAnimationFrame(function () {
        var stageH = stage.clientHeight || 400;
        var px = Math.max(10, (baseFs / dh) * stageH);
        el.style.fontSize = px + 'px';
        inner.style.fontSize = px + 'px';
        function fitIfNeeded() {
          if (!style.autoShrink) return;
          fitTextInBox(el, px);
        }
        if (document.fonts && document.fonts.load) {
          document.fonts.load(px + 'px ' + cssFamily).then(fitIfNeeded).catch(fitIfNeeded);
        } else {
          fitIfNeeded();
        }
      });
    });
  }

  function setActiveThumbIndex(idx) {
    var stage = qs('#usipic-pcfg-stage');
    if (!stage) return;
    stage.dataset.activeThumbIndex = String(idx);
    stage.classList.toggle('is-plain-preview', idx !== 0);
  }

  function setPreviewImage(url, opts) {
    opts = opts || {};
    var img = qs('#usipic-pcfg-preview-img');
    if (!img || !url) return;
    img.hidden = false;
    var resolved = getDisplayPreviewUrl(url);
    var runOverlays = opts.runOverlays !== false;
    var useCrossOrigin = runOverlays && !isPlainPreviewActive();

    if (img.src === resolved) {
      img.classList.remove('is-switching');
      if (runOverlays) runPreviewOverlaysIfNeeded();
      return;
    }

    var cached = preloadPreviewImage(url, useCrossOrigin);
    var switched = false;

    function finishSwitch() {
      if (switched) return;
      switched = true;
      img.classList.remove('is-switching');
      if (runOverlays) runPreviewOverlaysIfNeeded();
    }

    function applySrc() {
      if (useCrossOrigin) img.crossOrigin = 'anonymous';
      else img.removeAttribute('crossorigin');
      img.onerror = function () {
        var fallback = boot.media && boot.media[0] && boot.media[0].src;
        if (!fallback) {
          img.classList.remove('is-switching');
          return;
        }
        var fb = getDisplayPreviewUrl(fallback);
        if (img.src !== fb) {
          img.removeAttribute('crossorigin');
          img.src = fb;
        }
      };
      img.onload = function () {
        img.onload = null;
        finishSwitch();
      };
      img.src = resolved;
      if (img.complete && img.naturalWidth > 0) finishSwitch();
    }

    if (cached && cached.complete && cached.naturalWidth > 0) {
      applySrc();
      return;
    }

    img.classList.add('is-switching');
    if (cached) {
      var onReady = function () {
        cached.removeEventListener('load', onReady);
        cached.removeEventListener('error', onReady);
        applySrc();
      };
      cached.addEventListener('load', onReady);
      cached.addEventListener('error', onReady);
      return;
    }

    applySrc();
  }

  function getThumbItemsList() {
    var items = [];
    var baseUrl = getBasePreviewUrl();
    if (baseUrl) items.push({ src: baseUrl, thumb: baseUrl, alt: 'Design preview' });
    (boot.media || []).forEach(function (m) {
      if (m.type === 'image') items.push(m);
    });
    var seen = {};
    return items.filter(function (item) {
      if (!item.src || seen[item.src]) return false;
      seen[item.src] = true;
      return true;
    });
  }

  function buildThumbs(forceRebuild) {
    var items = getThumbItemsList();
    preloadThumbItems(items);
    if (forceRebuild) _thumbItemsKey = '';
    renderVisibleThumbs(forceRebuild);
    bindGalleryControls();
    updateGalleryControls();
  }

  function extractFrameCode(str) {
    if (!str) return '';
    var m = String(str).match(/\b(C\d+|F\d+|L\d+)\b/i);
    return m ? m[1].toUpperCase() : '';
  }

  function formatSizeChoiceLabel(name) {
    var s = String(name || '').trim();
    if (!s) return '';
    /* "24 x 16 in" / "24×16" / "24X16in" all render as "24X16in" so the
       label stays on one line. */
    s = s.replace(/\s*[x×]\s*/gi, 'X').replace(/\s*\bin\.?\s*$/i, '').trim();
    if (!s) return '';
    return s + 'in';
  }

  function resolveSizeChoiceLabel(ch) {
    if (!ch) return '';
    var name = ch.name || ch.label;
    if (name) return formatSizeChoiceLabel(name);
    var m = String(ch.id || '').match(/size_(\d+)x(\d+)/i);
    if (m) return formatSizeChoiceLabel(m[1] + '\u00d7' + m[2]);
    return String(ch.id || '');
  }

  /* The API names map-pin choices "Assorted Color Map Pins(Pin001) - 1 Box".
     The leading description just repeats the option label, so keep the word
     that introduces the code: "Pins(Pin001)- 1 Box". */
  function shortenPinsChoiceLabel(name) {
    var s = String(name || '').trim();
    if (!s) return '';
    var m = s.match(/^.*?\b(\w+)(\([^)]*\))(.*)$/);
    if (m) s = m[1] + m[2] + m[3];
    return s.replace(/\s*-\s*/g, '- ').replace(/\s+/g, ' ').trim();
  }

  function findActiveSizeOption() {
    if (!surface || !surface.options) return null;
    if (usesPerFrameSizeMatrix()) {
      var frameOpt = findFrameOption();
      if (frameOpt) {
        var fc = getFrameCodeFromChoice(getChoice(frameOpt, state.values[frameOpt.id]));
        if (fc) {
          var sized = getSizeOptionForFrameCode(fc);
          if (sized) return sized;
        }
      }
    }
    for (var i = 0; i < surface.options.length; i++) {
      var opt = surface.options[i];
      if (!isSizeOption(opt) || !isOptionVisible(opt)) continue;
      return opt;
    }
    return null;
  }

  function syncFrameCodeForSelectedSize() {
    if (usesPerFrameSizeMatrix()) return;
    var frameOpt = findFrameOption();
    if (!frameOpt) return;
    var prevCode = '';
    for (var i = 0; i < surface.options.length; i++) {
      var opt = surface.options[i];
      if (!isFrameLikeChoiceOption(opt)) continue;
      var ch = getChoice(opt, state.values[opt.id]);
      if (ch) {
        prevCode = getFrameCodeFromChoice(ch);
        if (prevCode) break;
      }
    }
    syncChoiceState(frameOpt);
    if (!prevCode) return;
    var enabled = getEnabledChoices(frameOpt);
    for (var j = 0; j < enabled.length; j++) {
      if (getFrameCodeFromChoice(enabled[j]) === prevCode) {
        state.values[frameOpt.id] = enabled[j].id;
        return;
      }
    }
  }

  function isSizeOption(opt) {
    if (!opt) return false;
    if (String(opt.id || '').indexOf('opt_size') === 0) return true;
    if (opt.role === 'size' || opt.optionRole === 'size') return true;
    if (/^size$/i.test(String(opt.label || '').trim())) return true;
    if (opt.choices && opt.choices.some(function (c) {
      return String(c.id || '').indexOf('size_') === 0;
    })) return true;
    return false;
  }

  function isPrintTypeOption(opt) {
    if (!opt) return false;
    if (opt.role === 'print' || opt.optionRole === 'print') return true;
    if (String(opt.id || '').indexOf('opt_print') === 0) return true;
    return /print\s*type/i.test(String(opt.label || ''));
  }

  /* The Amazon "confirm the preview image" text field. In this theme it is only
     an informational note: no input, no sample signature drawn on the preview,
     and it never blocks add-to-cart. */
  function isConfirmNoteOption(opt) {
    if (!opt || opt.type !== 'text') return false;
    return /confirm the preview image/i.test(String(opt.label || ''));
  }

  function getOptionDisplayOrder(opt) {
    var order = typeof opt.order === 'number' ? opt.order : 0;
    if (isSizeOption(opt)) return -20000 + order;
    if (isFrameLikeChoiceOption(opt)) return -10000 + order;
    /* Pushed to the very end of the option list. */
    if (isConfirmNoteOption(opt)) return 90000 + order;
    return order;
  }

  function sortOptionsForDisplay(options) {
    return options.slice().sort(function (a, b) {
      var da = getOptionDisplayOrder(a);
      var db = getOptionDisplayOrder(b);
      if (da !== db) return da - db;
      return String(a.id || '').localeCompare(String(b.id || ''));
    });
  }

  function isSizeChoiceOption(opt, ch) {
    if (isSizeOption(opt)) return true;
    if (ch && String(ch.id || '').indexOf('size_') === 0) return true;
    return false;
  }

  function resolveChoiceThumbUrl(ch, opt) {
    if (!ch || isSizeChoiceOption(opt, ch)) return '';
    var url = ch.thumbnailUrl || ch.previewUrl || ch.imageUrl || '';
    if (url) return url;

    var code = extractFrameCode(ch.name) || extractFrameCode(ch.id);
    if (code) return USIPIC_FRAME_ASSET_BASE + code + '.png';

    return '';
  }

  function isHorizontalCardChoiceOption(opt) {
    if (!opt) return false;
    return /map\s*pins?/i.test(String(opt.label || ''));
  }

  function appendChoiceCardRow(parent, opt) {
    choiceGridRenderIndex += 1;
    var row = document.createElement('div');
    var textOnly = isSizeOption(opt);
    var pins = isHorizontalCardChoiceOption(opt);
    var classes = ['usipic-pcfg__choices'];
    if (pins) {
      classes.push('usipic-pcfg__choices--pins', 'usipic-pcfg__choices--cols-4');
    } else if (textOnly) {
      classes.push('usipic-pcfg__choices--text-only', 'usipic-pcfg__choices--cols-7');
    } else if (isPrintTypeOption(opt)) {
      classes.push('usipic-pcfg__choices--print-type', 'usipic-pcfg__choices--cols-7');
    } else if (choiceGridRenderIndex <= 2) {
      classes.push('usipic-pcfg__choices--cols-7');
    } else {
      classes.push('usipic-pcfg__choices--cols-4');
    }
    row.className = classes.join(' ');
    getEnabledChoices(opt).forEach(function (ch) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'usipic-pcfg__choice' + (state.values[opt.id] === ch.id ? ' is-selected' : '');
        if (textOnly) btn.classList.add('usipic-pcfg__choice--text-only');
        var thumb = textOnly || pins ? '' : resolveChoiceThumbUrl(ch, opt);
        var priceLabel = textOnly || isFrameLikeChoiceOption(opt) ? '' : frameChoicePriceLabel(ch);
        var rawName = ch.name || ch.label || ch.id || '';
        var displayName = textOnly ? resolveSizeChoiceLabel(ch) : (pins ? shortenPinsChoiceLabel(rawName) : rawName);
        var materialLabel = !textOnly && !pins && isPrintTypeOption(opt) ? frameMaterialLabel(displayName) : '';
        btn.innerHTML =
          (thumb
            ? '<img class="usipic-pcfg__choice-thumb" src="' + escapeHtml(thumb) + '" alt="' + escapeHtml(displayName) + '" loading="lazy" onerror="this.remove()">'
            : '') +
          '<span class="usipic-pcfg__choice-body">' +
          '<span class="usipic-pcfg__choice-name">' + escapeHtml(displayName) + '</span>' +
          (materialLabel
            ? '<span class="usipic-pcfg__choice-material">' + escapeHtml(materialLabel) + '</span>'
            : '') +
          (priceLabel
            ? '<span class="usipic-pcfg__choice-price">' + escapeHtml(priceLabel) + '</span>'
            : '') +
          '</span>';
        btn.addEventListener('click', function () {
          state.values[opt.id] = ch.id;
          if (isSizeOption(opt)) {
            syncFrameCodeForSelectedSize();
          } else if (usesPerFrameSizeMatrix() && isFrameLikeChoiceOption(opt)) {
            var sk = getSelectedSizeKey();
            if (sk) setSelectedSizeKey(sk);
          }
          renderAll();
        });
        row.appendChild(btn);
      });
    parent.appendChild(row);
  }

  function renderUnifiedSizeBlock(root) {
    var catalog = getUnifiedSizeCatalog();
    if (!catalog.length) return;
    var selectedKey = getSelectedSizeKey();
    var block = document.createElement('div');
    block.className = 'usipic-pcfg__option';
    block.dataset.optionId = 'usipic_unified_size';
    block.innerHTML =
      '<div class="usipic-pcfg__option-head"><h3 class="usipic-pcfg__option-label">Size</h3></div>';

    choiceGridRenderIndex += 1;
    var row = document.createElement('div');
    row.className = 'usipic-pcfg__choices usipic-pcfg__choices--text-only usipic-pcfg__choices--cols-7';
    catalog.forEach(function (item) {
      var btn = document.createElement('button');
      btn.type = 'button';
      var isSel = normalizeKey(item.key) === normalizeKey(selectedKey);
      btn.className = 'usipic-pcfg__choice usipic-pcfg__choice--text-only' + (isSel ? ' is-selected' : '');
      btn.innerHTML =
        '<span class="usipic-pcfg__choice-body"><span class="usipic-pcfg__choice-name">' +
        escapeHtml(formatSizeChoiceLabel(item.name)) +
        '</span></span>';
      btn.addEventListener('click', function () {
        setSelectedSizeKey(item.key);
        syncFrameForSelectedSize();
        renderAll();
      });
      row.appendChild(btn);
    });
    block.appendChild(row);
    root.appendChild(block);
  }

  function renderOptions() {
    var root = qs('#usipic-pcfg-options');
    if (!root || !surface || !surface.options) return;
    root.innerHTML = '';
    choiceGridRenderIndex = 0;

    if (usesPerFrameSizeMatrix()) {
      ensureDefaultSizeKey();
      syncFrameForSelectedSize();
      renderUnifiedSizeBlock(root);
    }

    sortOptionsForDisplay(surface.options).forEach(function (opt) {
        if (isAggregatedSizeOption(opt)) return;
        if (!isOptionVisible(opt)) return;
        if (opt.choices && opt.choices.length && opt.type !== 'yes_no') {
          syncChoiceState(opt);
          if (!getEnabledChoices(opt).length) return;
        }

        var block = document.createElement('div');
        block.className = 'usipic-pcfg__option' + (isConfirmNoteOption(opt) ? ' usipic-pcfg__option--note' : '');
        block.dataset.optionId = opt.id;

        /* Map Pins heading gets the four-pin PNG (assets/usipic-pins.png, handed over
           through the bootstrap JSON) on the right. */
        var pinImage = boot && boot.pinImage ? boot.pinImage : '';
        var pinsIcons = (isHorizontalCardChoiceOption(opt) && pinImage)
          ? '<span class="usipic-pcfg__pins-icons" aria-hidden="true"><img class="usipic-pcfg__pins-img" src="' + escapeHtml(pinImage) + '" alt="" decoding="async"></span>'
          : '';
        var head = '<div class="usipic-pcfg__option-head' + (pinsIcons ? ' usipic-pcfg__option-head--pins' : '') + '"><h3 class="usipic-pcfg__option-label">' + escapeHtml(opt.label || '') + '</h3>' + pinsIcons + '</div>';
        /* The map-pins instruction only restates what the choice labels already
           say, so it is skipped for that option. The "DOUBLE-CHECK…" copy on the
           confirm note is skipped too. */
        var hint = (opt.instructions && !isHorizontalCardChoiceOption(opt) && !isConfirmNoteOption(opt)) ? '<p class="usipic-pcfg__option-hint">' + escapeHtml(opt.instructions) + '</p>' : '';
        block.innerHTML = head + hint;

        if (opt.type === 'yes_no') {
          var yn = document.createElement('div');
          yn.className = 'usipic-pcfg__yn';
          (opt.choices || [{ id: 'yes', name: 'Yes' }, { id: 'no', name: 'No' }])
            .filter(isChoiceEnabled)
            .forEach(function (ch) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'usipic-pcfg__choice' + (state.values[opt.id] === ch.id ? ' is-selected' : '');
            btn.innerHTML = '<span class="usipic-pcfg__choice-name">' + escapeHtml(ch.name || ch.id) + '</span>';
            btn.addEventListener('click', function () {
              state.values[opt.id] = ch.id;
              renderAll();
            });
            yn.appendChild(btn);
          });
          block.appendChild(yn);
        } else if (isFontOptionType(opt)) {
          renderFontColorPickers(block, opt);
        } else if (opt.type === 'font_color' || opt.type === 'text_color' || opt.type === 'color') {
          var colorWrap = document.createElement('div');
          colorWrap.className = 'usipic-pcfg__font-color';
          var standaloneColorLabel = document.createElement('p');
          standaloneColorLabel.className = 'usipic-pcfg__sub-label';
          standaloneColorLabel.textContent = 'Color';
          colorWrap.appendChild(standaloneColorLabel);
          appendColorPicker(colorWrap, opt.id, opt, null, null);
          block.appendChild(colorWrap);
        } else if (opt.type === 'image') {
          appendImageUploadRow(block, opt);
        } else if (opt.type === 'text' && !isConfirmNoteOption(opt)) {
          /* The confirm note keeps its heading but renders no input: it falls
             through this branch chain and is appended as a label-only block. */
          var textFonts = getTextFontChoices(opt);
          if (textFonts.length) {
            appendTextInputFontColorRow(block, opt, textFonts);
          } else if (getPresetColors(opt, null).length) {
            var onlyColorWrap = document.createElement('div');
            onlyColorWrap.className = 'usipic-pcfg__font-color';
            var onlyColorLabel = document.createElement('p');
            onlyColorLabel.className = 'usipic-pcfg__sub-label';
            onlyColorLabel.textContent = 'Color';
            onlyColorWrap.appendChild(onlyColorLabel);
            appendColorPicker(onlyColorWrap, opt.id, opt, null, null);
            block.appendChild(onlyColorWrap);

            var inpOnly = document.createElement(getEffectiveLines(opt) > 1 ? 'textarea' : 'input');
            inpOnly.className = 'usipic-pcfg__field';
            if (getEffectiveLines(opt) > 1) inpOnly.rows = getEffectiveLines(opt);
            else inpOnly.type = 'text';
            inpOnly.value = state.values[opt.id] || '';
            inpOnly.placeholder = opt.sampleText || '';
            if (opt.maxChars) inpOnly.maxLength = opt.maxChars;
            inpOnly.addEventListener('input', function () {
              state.values[opt.id] = inpOnly.value;
              refreshDesignPreview();
            });
            block.appendChild(inpOnly);
          } else {
            var inp = document.createElement(getEffectiveLines(opt) > 1 ? 'textarea' : 'input');
            inp.className = 'usipic-pcfg__field';
            if (getEffectiveLines(opt) > 1) inp.rows = getEffectiveLines(opt);
            else inp.type = 'text';
            inp.value = state.values[opt.id] || '';
            inp.placeholder = opt.sampleText || '';
            if (opt.maxChars) inp.maxLength = opt.maxChars;
            inp.addEventListener('input', function () {
              state.values[opt.id] = inp.value;
              refreshDesignPreview();
            });
            block.appendChild(inp);
          }
        } else if (isCardChoiceOption(opt)) {
          appendChoiceCardRow(block, opt);
        } else if (getEnabledChoices(opt).length && !isConfirmNoteOption(opt)) {
          appendChoiceCardRow(block, opt);
        }

        root.appendChild(block);
      });
  }
 
  function updatePreviewFromState() {
    var url = getBasePreviewUrl();
    if (url) setPreviewImage(url);
    else {
      updateCoverOverlays();
      renderImageLayers();
      renderTextOverlays();
    }
  }

  function renderAll() {
    syncDesignStageMetrics();
    renderOptions();
    updatePriceUI(pickActivePricing());
    ensureDesignPreviewActive();
    updatePreviewFromState();
    updateCoverOverlays();
    renderImageLayers();
    renderTextOverlays();
    buildThumbs(false);
  }

  var resizeTimer;
  function onPreviewResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      updateCoverOverlays();
      renderImageLayers();
      renderTextOverlays();
    }, 80);
  }

  function cartPropertyLabel(opt) {
    if (!opt) return 'Option';
    if (opt.type === 'frame_material' || isFrameLikeChoiceOption(opt)) return 'Frame style';
    if (opt.type === 'yes_no') return 'Customize map title';
    if (isFontOptionType(opt)) return 'Font';
    if (opt.type === 'font_color' || opt.type === 'text_color' || opt.type === 'color') return 'Font color';
    if (opt.type === 'image') return String(opt.label || 'Image').slice(0, 40);
    if (opt.type === 'text') {
      if (opt.displayLogic) return 'Map title';
      return String(opt.label || 'Text').slice(0, 40);
    }
    return String(opt.label || opt.id).slice(0, 40);
  }

  function colorCartLabel(colorCh) {
    if (!colorCh) return '';
    if (colorCh.id === COLOR_CUSTOM_ID) return colorCh.color || colorCh.fontColor || '';
    return colorCh.name || colorCh.color || colorCh.fontColor || '';
  }

  function appendFontColorCartProps(props, opt) {
    var fonts = getFontChoices(opt);
    var fontCh = getChoiceFromList(fonts, state.values[opt.id]);
    if (fontCh) props[cartPropertyLabel(opt)] = fontCh.name || fontCh.fontFamily || state.values[opt.id];
    if (getPresetColors(opt, fontCh).length) {
      var colorCh = resolveSelectedColor(opt.id, opt, fontCh);
      if (colorCh) props['Font color'] = colorCartLabel(colorCh);
    }
  }

  function buildLineItemProperties() {
    var pricing = pickActivePricing();
    var props = {
      _configurator_sku: boot.configSku || '',
      _customization_json: buildCustomizationJsonForCart(),
      _image_storage: 'shopify_cdn',
    };

    if (boot.configSku) {
      props['Configurator SKU'] = boot.configSku;
    }
    if (pricing && pricing.priceCents) {
      props['Configured price'] = formatMoney(pricing.priceCents);
      /* The cart renders this line's total and subtotal from this value. It has
         to be what the line actually charges — the variant price — so any add-on
         that is charged as its own cart line (map pins) is left out. */
      props['_configured_price_cents'] = String(pricing.priceCents - getNonFrameAddonCents());
    }
    if (pricing && pricing.usesPriceDiff) {
      if (pricing.matrixSizeCh && pricing.matrixSizeCh.priceDiff) {
        props['Size add-on'] = '+' + formatMoney(priceDiffCents(pricing.matrixSizeCh.priceDiff));
      } else if (pricing.choice && pricing.choice.priceDiff) {
        props['Frame add-on'] = '+' + formatMoney(priceDiffCents(pricing.choice.priceDiff));
      }
    }

    if (surface && surface.options) {
      surface.options.forEach(function (opt) {
        if (!isOptionVisible(opt)) return;
        var val = state.values[opt.id];
        var label = cartPropertyLabel(opt);
        if (isCardChoiceOption(opt) || (opt.choices && opt.choices.length)) {
          if (val == null || val === '') return;
          var ch = getChoice(opt, val);
          props[label] = ch ? ch.name : val;
          if (ch && ch.priceDiff && isAddonChoiceOption(opt)) {
            props[label + ' add-on'] = '+' + formatMoney(priceDiffCents(ch.priceDiff));
          }
        } else if (opt.type === 'yes_no') {
          if (val == null || val === '') return;
          props[label] = val === 'yes' ? 'Yes' : 'No';
        } else if (isFontOptionType(opt)) {
          if (!state.values[opt.id]) return;
          appendFontColorCartProps(props, opt);
        } else if (opt.type === 'font_color' || opt.type === 'text_color' || opt.type === 'color') {
          var colCh = resolveSelectedColor(opt.id, opt, null);
          if (colCh) props[label] = colorCartLabel(colCh);
        } else if (opt.type === 'image') {
          if (!hasImageForOption(opt)) return;
          var localImage = imageLocalFiles[opt.id];
          if (val && !localImage) props[label] = String(val);
          props[label + ' transform'] = state.values[stateKeyImageTransform(opt.id)] || JSON.stringify(defaultImageTransform());
        } else if (opt.type === 'text') {
          if (val == null || val === '') return;
          props[label] = String(val).slice(0, 100);
          var cartFonts = getTextFontChoices(opt);
          if (cartFonts.length) {
            var tFont = getChoiceFromList(cartFonts, state.values[stateKeyFont(opt.id)]);
            if (tFont) props[label + ' Font'] = tFont.name || tFont.fontFamily || '';
            if (getPresetColors(opt, tFont).length) {
              var tCol = resolveSelectedColor(opt.id, opt, tFont);
              if (tCol) props[label + ' Font color'] = colorCartLabel(tCol);
            }
          } else if (getPresetColors(opt, null).length) {
            var oCol = resolveSelectedColor(opt.id, opt, null);
            if (oCol) props[label + ' Font color'] = colorCartLabel(oCol);
          }
        } else {
          if (val == null || val === '') return;
          props[label] = String(val).slice(0, 100);
        }
      });
    }
    return props;
  }

  /** Product PDP only — cart preview file uploads (shared cart snippet keys, separate from Poster configurator). */
  var PCFG_CART_PREVIEW_FILE_KEYS = {
    'Customer preview': 'usipic-pcfg-preview.jpg',
    'Design preview': 'usipic-pcfg-design.jpg',
  };

  function canvasToJpegBlob(canvasEl, quality) {
    return new Promise(function (resolve) {
      if (!canvasEl || !canvasEl.width) return resolve(null);
      try {
        canvasEl.toBlob(
          function (blob) {
            resolve(blob || null);
          },
          'image/jpeg',
          quality != null ? quality : 0.88
        );
      } catch (e0) {
        resolve(null);
      }
    });
  }

  function loadImageForCanvas(url) {
    return new Promise(function (resolve) {
      if (!url) return resolve(null);
      var img = new Image();
      if (String(url).indexOf('blob:') !== 0) img.crossOrigin = 'anonymous';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        resolve(null);
      };
      img.src = url;
    });
  }

  function loadFirstCanvasImage(urls) {
    var list = (urls || []).filter(Boolean);
    var i = 0;
    function next() {
      if (i >= list.length) return Promise.resolve(null);
      var url = list[i++];
      return loadImageForCanvas(url).then(function (img) {
        return img || next();
      });
    }
    return next();
  }

  function canvasFontFamily(style) {
    if (!style) return 'Georgia, serif';
    if (style.cssFontFamily) return style.cssFontFamily;
    if (style.fontFamily) return '"' + style.fontFamily + '", Georgia, serif';
    return 'Georgia, serif';
  }

  function drawCanvasText(ctx, text, style, placement) {
    if (!ctx || !text || !placement) return;
    var displayText = String(text);
    if (style.textCase === 'uppercase') displayText = displayText.toUpperCase();
    else if (style.textCase === 'lowercase') displayText = displayText.toLowerCase();

    var x = placement.x;
    var y = placement.y;
    var w = placement.width;
    var h = placement.height;
    var maxPx = style.fontSize || 48;
    var weight = style.fontBold ? '700' : '400';
    var family = canvasFontFamily(style);
    var fs = maxPx;
    var guard = 0;

    ctx.save();
    ctx.fillStyle = style.fontColor || '#111111';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    while (guard < 80 && fs > 8) {
      ctx.font = weight + ' ' + fs + 'px ' + family;
      var metrics = ctx.measureText(displayText);
      var textH = fs * 1.15;
      if (metrics.width <= w && textH <= h) break;
      fs -= 1;
      guard += 1;
    }

    if (style.fontUnderline) {
      var underlineY = y + h / 2 + fs * 0.35;
      ctx.strokeStyle = ctx.fillStyle;
      ctx.lineWidth = Math.max(1, fs / 14);
      ctx.beginPath();
      ctx.moveTo(x + w * 0.1, underlineY);
      ctx.lineTo(x + w * 0.9, underlineY);
      ctx.stroke();
    }

    ctx.fillText(displayText, x + w / 2, y + h / 2);
    ctx.restore();
  }

  function drawAllTextOnCanvas(ctx) {
    if (!ctx || !surface || !surface.options) return;
    surface.options.forEach(function (opt) {
      if (opt.type !== 'text' || !isOptionVisible(opt)) return;
      var text = state.values[opt.id];
      if (!text || !String(text).trim()) return;
      var style = resolveTextStyleForOption(opt);
      if (!style.placement) return;
      drawCanvasText(ctx, text, style, style.placement);
    });
  }

  function preloadFontsForExport() {
    var jobs = [];
    if (!surface || !surface.options) return Promise.resolve();
    surface.options.forEach(function (opt) {
      if (opt.type !== 'text' || !isOptionVisible(opt)) return;
      var text = state.values[opt.id];
      if (!text || !String(text).trim()) return;
      var style = resolveTextStyleForOption(opt);
      jobs.push(loadFontResource(style));
      if (document.fonts && document.fonts.load) {
        var weight = style.fontBold ? '700' : '400';
        jobs.push(
          document.fonts.load(weight + ' ' + (style.fontSize || 32) + 'px ' + canvasFontFamily(style)).catch(function () {})
        );
      }
    });
    return Promise.all(jobs).then(function () {
      if (document.fonts && document.fonts.ready) return document.fonts.ready;
    });
  }

  function getPreviewBaseImageUrls() {
    var urls = [];
    var previewImg = qs('#usipic-pcfg-preview-img');
    if (previewImg && previewImg.src && previewImg.src.indexOf('data:') !== 0) urls.push(previewImg.src);
    var apiBase = getBasePreviewUrl();
    if (apiBase && urls.indexOf(apiBase) < 0) urls.push(apiBase);
    if (boot.media && boot.media.length) {
      boot.media.forEach(function (m) {
        if (m.type === 'image' && m.src && urls.indexOf(m.src) < 0) urls.push(m.src);
      });
    }
    return urls;
  }

  /** Composite design base + overlays + customer text for cart thumbnail (Product PDP only). */
  function exportProductConfiguratorPreviewBlob() {
    if (!surface || isPlainPreviewActive()) return Promise.resolve(null);
    var size = resolveDesignSize(surface);
    var dw = size.width;
    var dh = size.height;

    return preloadFontsForExport().then(function () {
      return loadFirstCanvasImage(getPreviewBaseImageUrls());
    }).then(function (baseImg) {
      if (!baseImg) return null;
      var canvas = document.createElement('canvas');
      canvas.width = dw;
      canvas.height = dh;
      var ctx = canvas.getContext('2d');
      if (!ctx) return null;
      drawImageContain(ctx, baseImg, dw, dh);
      return loadImageForCanvas(getTitleCoverUrl()).then(function (titleImg) {
        if (titleImg) drawImageContain(ctx, titleImg, dw, dh);
        return loadImageForCanvas(getFrameOverlayUrl());
      }).then(function (frameImg) {
        if (frameImg) drawImageContain(ctx, frameImg, dw, dh);
        return drawUploadedImagesOnCanvas(ctx, dw, dh);
      }).then(function () {
        drawAllTextOnCanvas(ctx);
        return canvasToJpegBlob(canvas, 0.88);
      });
    });
  }

  function collectProductCartPreviewFiles() {
    var imageFiles = collectImageCartFiles();
    return exportProductConfiguratorPreviewBlob()
      .catch(function () {
        return null;
      })
      .then(function (blob) {
        var files = Object.assign({}, imageFiles);
        if (blob) {
          files['Customer preview'] = blob;
          files['Design preview'] = blob;
        }
        return files;
      });
  }

  function findPinsOption() {
    if (!surface || !surface.options) return null;
    for (var i = 0; i < surface.options.length; i++) {
      var o = surface.options[i];
      if (o.type === 'dropdown' && /map pins/i.test(String(o.label || ''))) return o;
    }
    return null;
  }

  /* The map variant only carries the size + frame price, so a selected pin pack
     is charged by adding its own variant as a second cart line. The map of
     option id -> variant id comes from snippets/usipic-pin-addon.liquid. */
  function getSelectedPinVariantId() {
    var map = (boot && boot.pinVariants) || {};
    var opt = findPinsOption();
    if (!opt) return null;
    var ch = getChoice(opt, state.values[opt.id]);
    if (!ch) return null;
    var id = map[ch.id];
    return id ? Number(id) : null;
  }

  function postProductCartAdd(vid, quantity, properties, fileBlobs) {
    fileBlobs = fileBlobs || {};
    var pinVid = getSelectedPinVariantId();
    var fileKeys = Object.keys(fileBlobs).filter(function (key) {
      return !!resolveCartFileEntry(key, fileBlobs[key]);
    });
    var hasFiles = fileKeys.length > 0;
    if (hasFiles) {
      var fd = new FormData();
      fd.append('items[0][id]', String(vid));
      fd.append('items[0][quantity]', String(quantity));
      if (pinVid) {
        fd.append('items[1][id]', String(pinVid));
        fd.append('items[1][quantity]', '1');
      }
      Object.keys(properties).forEach(function (key) {
        if (resolveCartFileEntry(key, fileBlobs[key])) return;
        fd.append('items[0][properties][' + key + ']', properties[key]);
      });
      fileKeys.forEach(function (key) {
        var resolved = resolveCartFileEntry(key, fileBlobs[key]);
        if (resolved) {
          fd.append('items[0][properties][' + key + ']', resolved.blob, resolved.filename);
        }
      });
      return fetch(cartAddUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: fd,
      });
    }
    var items = [{ id: Number(vid), quantity: quantity, properties: properties }];
    if (pinVid) items.push({ id: pinVid, quantity: 1 });
    return fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    });
  }

  function parseProductCartAddResponse(res) {
    return res.text().then(function (text) {
      var body = null;
      try {
        body = text ? JSON.parse(text) : null;
      } catch (e0) {
        throw new Error('Cart error (' + res.status + ')');
      }
      if (!res.ok) {
        throw new Error((body && (body.description || body.message)) || 'Cart error (' + res.status + ')');
      }
      return body;
    });
  }

  function bindAtc() {
    var btn = qs('#usipic-pcfg-atc');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var variant = pickActiveVariant();
      if (!variant || !variant.available) {
        setStatus('Please select an available option.', true);
        return;
      }
      var imgCheck = validateRequiredImages();
      if (!imgCheck.ok) {
        setStatus(imgCheck.message, true);
        return;
      }
      btn.disabled = true;
      setStatus('Adding to cart...', false);
      collectProductCartPreviewFiles()
        .then(function (fileBlobs) {
          var properties = buildLineItemProperties();
          return postProductCartAdd(variant.id, 1, properties, fileBlobs);
        })
        .then(parseProductCartAddResponse)
        .then(function () {
          getImageOptions().forEach(function (opt) {
            revokeLocalImage(opt.id);
          });
          setStatus('Added to cart.', false);
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(function (err) {
          setStatus(err.message || 'Could not add to cart', true);
        })
        .finally(function () {
          updatePriceUI(pickActivePricing());
        });
    });
  }

  function initDesignModePreview() {
    surface = { designWidth: 1200, designHeight: 1200, options: [] };
    if (boot.variants && boot.variants.length) {
      state.variantId = boot.variants[0].id;
    }
    var optsEl = qs('#usipic-pcfg-options');
    if (optsEl) {
      optsEl.innerHTML =
        '<p class="usipic-pcfg__editor-note">Theme editor preview. Customization options load on the live storefront.</p>';
    }
    bindAtc();
    renderAll();
    setLoading(false);
  }

  function init() {
    boot = readBoot();
    if (!boot) return;

    if (boot.designMode || (window.Shopify && window.Shopify.designMode)) {
      initDesignModePreview();
      return;
    }

    bindAtc();
    bindFontMenuOutsideClick();
    setLoading(true);
    showError('');

    fetchConfig()
      .then(function () {
        initDefaultValues();
        renderAll();
        setLoading(false);
      })
      .catch(function (err) {
        setLoading(false);
        showError('Could not load product configuration. ' + (err.message || ''));
        updatePriceUI(pickActivePricing());
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

